import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";

import { db, storage } from "./firebase";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd"; // <-- ADD THIS IMPORT
import { doc, onSnapshot, getDoc, setDoc, collection, getDocs, updateDoc, serverTimestamp, Timestamp, arrayUnion, runTransaction } from "firebase/firestore";
import { CapacitorHttp } from "@capacitor/core";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import './styles.css';
import { useParams } from "react-router-dom";
import { isProfane } from './profanityFilter';
import "./styles.css"; // keep your existing stylesheet imports
import { PlayerAnswerFitText } from "./playerAnswerFitText";

// Player.js — Top countdown identical to Quizscreen (uses the shared combo)
import { ThermoTimerBar } from "./CountdownTimerBars";
import {
  DEFAULT_TEAM_ROUND_REVEAL_PAUSE_SEC,
  getTeamRoundChoiceSettingsFromGameData,
} from "./roundTagChoice";
import PhoneSetupTipsCard from "./PhoneSetupTipsCard";
import {
  shouldShowPhoneSetupTips,
  shouldUseNativeSelfiePicker,
  isLikelyIosSimulator,
} from "./playerEnvironment";
import {
  pickTeamSelfieFromCamera,
  pickTeamSelfieFromLibrary,
  compressSelfieFile,
  formatSelfieFileSize,
  isSelfiePickCancelled,
  isCameraPluginUnavailable,
} from "./teamSelfiePicker";
import { brainScreenStyle, wizardTitleStyle } from "./brainBackground";
import {
  computeVanishingIndexesFromPlan,
  shouldDeferTimerUntilAnswersGate,
} from "./quizHelpers";

// Player answer pills: match height of one row in a 4-option multiple-choice list (60vh, 2.5vh gaps).
const PLAYER_MC_ANSWER_SLOTS = 4;
const PLAYER_ANSWER_ROW_HEIGHT = `calc((60vh - ${(PLAYER_MC_ANSWER_SLOTS - 1) * 2.5}vh) / ${PLAYER_MC_ANSWER_SLOTS})`;
/** ThermoTimerBar is position:fixed (bottom 5vh) — content must not sit underneath it */
const PLAYER_TIMER_BAR_RESERVE =
  "calc(5vh + 51px + env(safe-area-inset-bottom, 0px))";

/** Cap pill height when many options so rows don't stretch to fill the whole screen. */
function getPlayerAnswerRowLayout(optionCount) {
  const n = Math.max(1, optionCount);
  const gapVh = n >= 6 ? 1.1 : n >= 5 ? 1.35 : n >= 4 ? 1.8 : 2.5;
  const areaVh = 50;
  const rowVh = Math.min(13.25, (areaVh - (n - 1) * gapVh) / n);
  return { gap: `${gapVh}vh`, rowHeight: `${rowVh}vh` };
}


// ——— Player background helpers (copy kept local to Player.js) ———
// ——— Player background helpers ———
function ensurePreloadLink(url) {
  if (!url) return;
  const key = `preload-${url}`;
  if (document.querySelector(`link[data-preload="${key}"]`)) return;
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = url;
  link.setAttribute("data-preload", key);
  document.head.appendChild(link);
}

function buildBackgroundCSS(url, isDisplayLike = true) {
  if (!url) return undefined;
  // If you want the darker host-style overlay on non-display slides,
  // flip isDisplayLike based on the question type below.
  return isDisplayLike
    ? `url(${url}) center center / cover no-repeat`
    : `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url(${url}) center center / cover no-repeat`;
}

// Helper: Check if text is wrapped in square brackets like "[xxxxxx]"
function isTextInBrackets(text) {
  if (!text || typeof text !== 'string') return false;
  const trimmed = text.trim();
  return /^\[.*\]$/.test(trimmed);
}

// Helper: Extract text from brackets (remove brackets) for display
function extractTextFromBrackets(text) {
  if (!text || typeof text !== 'string') return text;
  const trimmed = text.trim();
  if (/^\[.*\]$/.test(trimmed)) {
    return trimmed.slice(1, -1); // Remove first [ and last ]
  }
  return text;
}

function readFirestoreRestValue(value = {}) {
  if ("stringValue" in value) return value.stringValue;
  if ("integerValue" in value) return Number(value.integerValue);
  if ("doubleValue" in value) return Number(value.doubleValue);
  if ("booleanValue" in value) return value.booleanValue;
  if ("nullValue" in value) return null;
  if ("timestampValue" in value) return value.timestampValue;
  if ("arrayValue" in value) {
    return (value.arrayValue.values || []).map(readFirestoreRestValue);
  }
  if ("mapValue" in value) {
    return Object.fromEntries(
      Object.entries(value.mapValue.fields || {}).map(([key, child]) => [key, readFirestoreRestValue(child)])
    );
  }
  return undefined;
}

function readFirestoreRestDocument(json) {
  if (!json?.fields) return null;
  return Object.fromEntries(
    Object.entries(json.fields).map(([key, value]) => [key, readFirestoreRestValue(value)])
  );
}

function firestoreTimestampToMs(value) {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (value instanceof Date) return value.getTime();
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  return 0;
}

function getQuestionDurationSeconds(qData, fallbackTimer = 0) {
  const raw = qData?.TIME ?? qData?.time ?? fallbackTimer ?? 0;
  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
}

function getSyncedRemainingSeconds(gameData, qData) {
  const dbTimer = Number(gameData?.timer ?? 0) || 0;
  const duration = getQuestionDurationSeconds(qData, dbTimer);
  const startedAtMs =
    firestoreTimestampToMs(gameData?.currentAt) ||
    firestoreTimestampToMs(gameData?.questionStartTimeServer) ||
    firestoreTimestampToMs(gameData?.questionStartTime);
  if (!gameData?.timerActive) return Math.max(0, dbTimer);
  if (!startedAtMs || !duration) return Math.max(0, dbTimer);

  const elapsedSeconds = (Date.now() - startedAtMs) / 1000;
  return Math.max(0, Math.ceil(duration - elapsedSeconds));
}

async function fetchGameDocumentViaRest(gameCode) {
  const response = await fetch(
    `https://firestore.googleapis.com/v1/projects/bobquiz-272a7/databases/(default)/documents/games/${encodeURIComponent(gameCode)}?key=AIzaSyC-LC0meTRyf50zFREASG6DsLKl4WU1DF8`
  );
  if (!response.ok) {
    throw new Error(`REST game ${response.status}`);
  }
  return readFirestoreRestDocument(await response.json());
}

async function fetchFirestoreDocViaRest(path) {
  const response = await fetch(
    `https://firestore.googleapis.com/v1/projects/bobquiz-272a7/databases/(default)/documents/${path}?key=AIzaSyC-LC0meTRyf50zFREASG6DsLKl4WU1DF8`
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`REST doc ${response.status}`);
  }
  return readFirestoreRestDocument(await response.json());
}

/** Like fetchFirestoreDocViaRest, plus the document's updateTime (for write preconditions). */
async function fetchFirestoreDocWithTimeViaRest(path) {
  const response = await fetch(
    `https://firestore.googleapis.com/v1/projects/bobquiz-272a7/databases/(default)/documents/${path}?key=AIzaSyC-LC0meTRyf50zFREASG6DsLKl4WU1DF8`
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`REST doc ${response.status}`);
  }
  const json = await response.json();
  return { data: readFirestoreRestDocument(json) || {}, updateTime: json.updateTime };
}

async function fetchTeamsViaRest(gameCode) {
  const response = await fetch(
    `https://firestore.googleapis.com/v1/projects/bobquiz-272a7/databases/(default)/documents/games/${encodeURIComponent(gameCode)}/teams?key=AIzaSyC-LC0meTRyf50zFREASG6DsLKl4WU1DF8`
  );
  if (!response.ok) {
    throw new Error(`REST teams ${response.status}`);
  }
  const json = await response.json();
  return (json.documents || []).map((doc) => ({
    id: doc.name?.split("/").pop() || "",
    data: readFirestoreRestDocument(doc) || {},
  }));
}

async function fetchAnswersViaRest(gameCode) {
  // A full quiz has hundreds of answers, more than one REST page holds, so follow nextPageToken.
  const rows = [];
  let pageToken = "";
  do {
    const response = await fetch(
      `https://firestore.googleapis.com/v1/projects/bobquiz-272a7/databases/(default)/documents/games/${encodeURIComponent(gameCode)}/answers?key=AIzaSyC-LC0meTRyf50zFREASG6DsLKl4WU1DF8&pageSize=300${pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : ""}`
    );
    if (response.status === 404) return rows;
    if (!response.ok) {
      throw new Error(`REST answers ${response.status}`);
    }
    const json = await response.json();
    (json.documents || []).forEach((doc) => rows.push({
      id: doc.name?.split("/").pop() || "",
      data: readFirestoreRestDocument(doc) || {},
    }));
    pageToken = json.nextPageToken || "";
  } while (pageToken);
  return rows;
}

async function blobToBase64(blob) {
  const buffer = blob instanceof Blob ? await blob.arrayBuffer() : blob;
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

async function uploadSelfieViaRest(gameCode, teamName, file) {
  const bucket = "bobquiz-272a7.firebasestorage.app";
  const storagePath = `selfies/${gameCode}/${teamName}.jpg`;
  const contentType = file.type || "image/jpeg";
  const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o?uploadType=media&name=${encodeURIComponent(storagePath)}`;

  if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.()) {
    const base64 = await blobToBase64(file);
    const response = await CapacitorHttp.post({
      url,
      headers: { "Content-Type": contentType },
      data: base64,
      dataType: "file",
      connectTimeout: 45000,
      readTimeout: 45000,
    });
    if (response.status < 200 || response.status >= 300) {
      const detail = typeof response.data === "string" ? response.data : JSON.stringify(response.data || "");
      throw new Error(`REST upload ${response.status}: ${detail.slice(0, 120)}`);
    }
    const metadata = typeof response.data === "string" ? JSON.parse(response.data) : response.data;
    const token = metadata.downloadTokens || metadata.metadata?.firebaseStorageDownloadTokens || "";
    return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(storagePath)}?alt=media${token ? `&token=${token}` : ""}`;
  }

  const body = file instanceof Blob ? await file.arrayBuffer() : file;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 45000);
  let response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": contentType },
      body,
      signal: controller.signal,
    });
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error("Upload timed out. Check your connection and try again.");
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`REST upload ${response.status}: ${text.slice(0, 120)}`);
  }
  const metadata = await response.json();
  const token = metadata.downloadTokens || metadata.metadata?.firebaseStorageDownloadTokens || "";
  return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(storagePath)}?alt=media${token ? `&token=${token}` : ""}`;
}

async function setTeamViaRest(gameCode, teamName, selfieUrl) {
  const response = await fetch(
    `https://firestore.googleapis.com/v1/projects/bobquiz-272a7/databases/(default)/documents/games/${encodeURIComponent(gameCode)}/teams/${encodeURIComponent(teamName)}?key=AIzaSyC-LC0meTRyf50zFREASG6DsLKl4WU1DF8`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fields: {
          team: { stringValue: teamName },
          selfieUrl: { stringValue: selfieUrl },
          joinedAt: { integerValue: String(Date.now()) },
        },
      }),
    }
  );
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`REST team ${response.status}: ${text.slice(0, 120)}`);
  }
}

function toFirestoreRestValue(value) {
  if (value instanceof Date) return { timestampValue: value.toISOString() };
  if (value && typeof value.toDate === "function") return { timestampValue: value.toDate().toISOString() };
  if (Array.isArray(value)) return { arrayValue: { values: value.map(toFirestoreRestValue) } };
  if (value && typeof value === "object") {
    return {
      mapValue: {
        fields: Object.fromEntries(
          Object.entries(value)
            .filter(([, child]) => child !== undefined)
            .map(([key, child]) => [key, toFirestoreRestValue(child)])
        ),
      },
    };
  }
  if (typeof value === "string") return { stringValue: value };
  if (typeof value === "boolean") return { booleanValue: value };
  if (typeof value === "number") {
    return Number.isInteger(value) ? { integerValue: String(value) } : { doubleValue: value };
  }
  if (value === null) return { nullValue: null };
  return { stringValue: String(value) };
}

async function setFirestoreDocViaRest(path, data) {
  const response = await fetch(
    `https://firestore.googleapis.com/v1/projects/bobquiz-272a7/databases/(default)/documents/${path}?key=AIzaSyC-LC0meTRyf50zFREASG6DsLKl4WU1DF8`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fields: Object.fromEntries(
          Object.entries(data)
            .filter(([, value]) => value !== undefined)
            .map(([key, value]) => [key, toFirestoreRestValue(value)])
        ),
      }),
    }
  );
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`REST write ${response.status}: ${text.slice(0, 120)}`);
  }
}

/**
 * Updates only the given fields (updateMask), leaving the rest of the document untouched — like setDoc merge.
 * Pass { updateTime } to fail (409/412) if the document changed since it was read — a one-document transaction.
 */
async function updateFirestoreFieldsViaRest(path, data, { updateTime } = {}) {
  const entries = Object.entries(data).filter(([, value]) => value !== undefined);
  const mask = entries.map(([key]) => `updateMask.fieldPaths=${encodeURIComponent(key)}`).join("&");
  const precondition = updateTime ? `&currentDocument.updateTime=${encodeURIComponent(updateTime)}` : "";
  const response = await fetch(
    `https://firestore.googleapis.com/v1/projects/bobquiz-272a7/databases/(default)/documents/${path}?key=AIzaSyC-LC0meTRyf50zFREASG6DsLKl4WU1DF8&${mask}${precondition}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fields: Object.fromEntries(entries.map(([key, value]) => [key, toFirestoreRestValue(value)])),
      }),
    }
  );
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`REST update ${response.status}: ${text.slice(0, 120)}`);
  }
}

async function joinTeamWithSelfie({ gameCode, teamName, selfieFile }) {
  if (typeof window !== "undefined" && window.Capacitor) {
    const downloadURL = await uploadSelfieViaRest(gameCode, teamName, selfieFile);
    await setTeamViaRest(gameCode, teamName, downloadURL);
    return downloadURL;
  }

  const selfieRef = ref(storage, `selfies/${gameCode}/${teamName}.jpg`);
  await uploadBytes(selfieRef, selfieFile);
  const downloadURL = await getDownloadURL(selfieRef);
  await setDoc(doc(db, "games", gameCode, "teams", teamName), {
    team: teamName,
    selfieUrl: downloadURL,
    joinedAt: Date.now(),
  });
  return downloadURL;
}

function PlayerBackground({ q }) {
  const backgroundUrl =
    q?.BACKGROUND_URL || q?.background || q?.P1_URL || q?.p1 || "";

  const rawType = String(q?.type || q?.CAT || "").trim().toUpperCase();
  const displayAliases = new Set([
    "DISPLAY", "SLIDE", "DEFAULT", "STARTROUND", "ENDROUND", "SHOWROUNDS",
    "INTRO", "INTRODUCTION", "JOIN QUIZ", "END QUIZ", "BREAK", "BREAK TIME"
  ]);
  const isDisplayLike = displayAliases.has(rawType);

  React.useEffect(() => {
    if (backgroundUrl) ensurePreloadLink(backgroundUrl);
  }, [backgroundUrl]);

  if (!backgroundUrl) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: -1,                        // ⬅️ force it *behind* all normal content
        background: buildBackgroundCSS(backgroundUrl, isDisplayLike),
        pointerEvents: "none"
      }}
    />
  );
}





// --- OUTOFORDER: drag-to-reorder pills (glossy, same look as MCQ) ---
function OutOfOrderList({
  options = [],           // array of strings
  seed = 1,               // for deterministic shuffle per player
  onOrderChange = () => {} // callback(newOrderIndices)
}) {
  const [order, setOrder] = useState(() => {
    // start shuffled (but stable per player/question)
    const idx = options.map((_, i) => i);
    // simple seeded shuffle (mulberry32)
    let a = idx.slice();
    let s = seed >>> 0;
    const rand = () => {
      s |= 0; s = (s + 0x6D2B79F5) | 0;
      let t = Math.imul(s ^ (s >>> 15), 1 | s);
      t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  });

  // dragging state
  const draggingIndex = useRef(null);

  const handleDragStart = (e) => {
    const from = Number(e.currentTarget.dataset.idx);
    draggingIndex.current = from;
    e.dataTransfer.effectAllowed = "move";
    // a small ghost is enough; some browsers need data to enable DnD
    e.dataTransfer.setData("text/plain", String(from));
  };

  const handleDragOver = (e) => {
    // allow drop
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, toIndex) => {
    e.preventDefault();
    const fromIndex = draggingIndex.current;
    draggingIndex.current = null;
    if (fromIndex == null || fromIndex === toIndex) return;

    // reorder our `order` array (shift other items to make room)
    setOrder((prev) => {
      const a = prev.slice();
      const [moved] = a.splice(fromIndex, 1);
      a.splice(toIndex, 0, moved);
      onOrderChange(a);   // publish the new order (indices into options)
      return a;
    });
  };

  const handleDragEnd = () => {
    draggingIndex.current = null;
  };

  // render as the same glossy pills as MCQ: .answer-list > .answer-option.option-a..f
  return (
    <ol className="answer-list" style={{ listStyle: "none", padding: 0}}>
      {order.map((optIdx, visualIdx) => {
        const letterClass = `option-${String.fromCharCode(97 + (visualIdx % 6))}`; // a..f
        return (
          <li
            key={`${optIdx}-${visualIdx}`}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, visualIdx)}
            style={{ display: "flex", justifyContent: "center" }}
          >
            <div
              className={`answer-option ${letterClass}`}
              style={{
                userSelect: "none",
                cursor: "grab",
                width: "min(900px, 86vw)",
                textAlign: "center",
                fontWeight: 900,
              }}
              draggable
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              data-idx={visualIdx}   // visual index (position in current order)
              aria-label={`Move ${options[optIdx]}`}
              role="listitem"
            >
              {options[optIdx]}
            </div>
          </li>
        );
      })}
    </ol>
  );
}


function getMultiAnswerSuffix(q) {
  if ((q.type || "").toUpperCase() !== "MULTIANSWER") return "";
  // Count answers with brackets
  const count = q.answers.filter(a => /^\s*\[.*?\]\s*$/.test(a)).length;
  if (count > 1) {
    return ` (SELECT ${count})`;
  }
  return "";
}

// Player.js — NEW: simple waiting placeholder
const WAITING_FOR_QUIZMASTER_TEXT = "Waiting for Quizmaster to start the quiz";

function isLobbyQuestion(current, q) {
  const rawType = String(q?.type || q?.CAT || "").trim().toUpperCase();
  return current === 0 || rawType === "JOIN QUIZ";
}

function getPlayerQuestionDisplayText(q, isBlock = false) {
  if (!q?.question) return "";
  let text = isBlock
    ? q.question.replace(/^\{B\}\s*/, "")
    : q.question
        .replace(/^Q\d+:\s*/, "")
        .replace(/\{(?!LB\})[^}]*\}/g, "")
        .replace(/\s{2,}/g, " ")
        .trim();

  const rawTypeUpper = String(q?.type || q?.CAT || "").toUpperCase();
  const isStartEndOrDisplay =
    rawTypeUpper === "STARTROUND" ||
    rawTypeUpper === "ENDROUND" ||
    rawTypeUpper === "DISPLAY";

  if (isStartEndOrDisplay && isTextInBrackets(text)) {
    text = extractTextFromBrackets(text);
  }
  return text;
}

/** Smaller font for longer questions so answer pills keep more vertical space. */
function computePlayerQuestionFontSize(isBlock, plainText = "") {
  if (isBlock) return "clamp(1.95rem, 6.5vw, 3.51rem)";

  const len = plainText.length;
  const lineCount = Math.max(1, plainText.split("{LB}").length);
  const score = len + (lineCount - 1) * 36;

  if (score > 200) return "clamp(1.05rem, 2.7vw, 1.9rem)";
  if (score > 160) return "clamp(1.15rem, 2.95vw, 2.1rem)";
  if (score > 120) return "clamp(1.25rem, 3.15vw, 2.3rem)";
  if (score > 85) return "clamp(1.35rem, 3.35vw, 2.55rem)";
  return "clamp(1.43rem, 3.51vw, 2.86rem)";
}

function getPlayerQuestionPillStyle(isBlock = false, { isFirst = false, compactBottom = false, plainText = "" } = {}) {
  const textLen = plainText.length;
  const lineCount = Math.max(1, plainText.split("{LB}").length);
  const longQuestion = !isBlock && (textLen > 85 || lineCount > 2);
  const bottomMargin = compactBottom
    ? "1em"
    : isBlock
      ? "0.8em"
      : longQuestion
        ? "2.5vh"
        : "4vh";
  return {
    position: "relative",
    zIndex: 2,
    flexShrink: 0,
    fontSize: computePlayerQuestionFontSize(isBlock, plainText),
    lineHeight: isBlock ? 1.35 : longQuestion ? 1.14 : 1.19,
    width: "calc(100vw - 10px)",
    maxWidth: "calc(100vw - 10px)",
    borderRadius: "0.9em",
    textAlign: "center",
    margin: isFirst
      ? `calc(env(safe-area-inset-top, 0px) + 8vh) auto ${bottomMargin} auto`
      : `0 auto ${bottomMargin} auto`,
    fontWeight: 900,
    fontFamily: "'QuizPlayerMontserrat', 'Montserrat', 'Arial Black', Arial, sans-serif",
    textShadow: "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
    boxSizing: "border-box",
    background: "rgba(18, 20, 28, 0.58)",
    color: "#fff",
    whiteSpace: isBlock ? "pre-line" : "normal",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    wordBreak: "break-word",
    hyphens: "none",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    padding: longQuestion ? "0.55em 0.65em" : "0.65em 0.8em",
  };
}

// --- Add this function ---
/**
 * Calculates the similarity between two strings based on Levenshtein distance,
 * after converting both to lowercase and removing spaces.
 * @param {string} s1 First string.
 * @param {string} s2 Second string.
 * @returns {number} Similarity score between 0 (completely different) and 1 (identical).
 */
function calculateNormalizedSimilarity(s1, s2) {
  const normalize = (str) => (str || "").toLowerCase().replace(/\s+/g, ''); // Lowercase, remove spaces
  const str1 = normalize(s1);
  const str2 = normalize(s2);

  if (str1 === str2) return 1; // Identical after normalization
  if (!str1.length || !str2.length) return 0; // One is empty

  const len1 = str1.length;
  const len2 = str2.length;
  const matrix = Array(len1 + 1).fill(null).map(() => Array(len2 + 1).fill(0));

  for (let i = 0; i <= len1; i++) matrix[i][0] = i;
  for (let j = 0; j <= len2; j++) matrix[0][j] = j;

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,      // Deletion
        matrix[i][j - 1] + 1,      // Insertion
        matrix[i - 1][j - 1] + cost // Substitution
      );
    }
  }

  const distance = matrix[len1][len2];
  const maxLength = Math.max(len1, len2);
  return 1 - (distance / maxLength);
}


// Helper functions for question type rules card (copied from Quizscreen.js)
/**
 * Original answer index for each on-screen position. TRUE/FALSE is always shown FALSE then TRUE,
 * whatever the shuffle; everything else follows answersOrder. Use this for display, scoring and saving
 * so a tap is always recorded as the answer the player saw.
 */
function getDisplayAnswerOrder(q) {
  const answers = Array.isArray(q?.answers) ? q.answers : [];
  const baseOrder =
    Array.isArray(q?.answersOrder) && q.answersOrder.length ? [...q.answersOrder] : answers.map((_, i) => i);
  if (normalizeType(q) !== "TRUE/FALSE") return baseOrder;
  const findIdx = (word) => baseOrder.find((idx) => String(answers[idx]).trim().toUpperCase().includes(word));
  const tf = [findIdx("FALSE"), findIdx("TRUE")].filter((i) => typeof i === "number");
  return (tf.length ? tf : baseOrder.slice(0, 2)).slice(0, 2);
}

function normalizeType(q) {
  const raw = String(q?.type || q?.CAT || "").trim().toUpperCase();

  const displayAliases = new Set([
    "DISPLAY", "SLIDE", "DEFAULT", "STARTROUND", "ENDROUND", "SHOWROUNDS",
    "JOIN QUIZ", "INTRODUCTION", "END QUIZ", "BREAK TIME", "DEFAULT CATEGORY"
  ]);

  if (displayAliases.has(raw)) return "DISPLAY";

  const known = [
    "MULTICHOICE", "TRUE/FALSE", "TRUEFALSE", "MULTIANSWER",
    "NEARESTANSWER", "JACKPOT", "TYPE", "BREADCRUMBS", "BETANSWER", "WHEEL", "PICTURE",
    "AUDIO", "VIDEO", "PUZZLE", "VANISHINGCHOICE", "OUTOFORDER" 
  ];
  if (known.includes(raw)) return raw.replace("TRUEFALSE", "TRUE/FALSE");

  const answers = Array.isArray(q?.answers)
    ? q.answers
    : [q?.A1, q?.A2, q?.A3, q?.A4, q?.A5, q?.A6].filter(v => v !== undefined);
  const noAnswers = answers.every(a => String(a ?? "").trim() === "");
  const t = parseInt(q?.time ?? q?.TIME ?? 0, 10) || 0;
  if (noAnswers && t === 0) return "DISPLAY";

  return raw || "DISPLAY";
}

function getTypeTheme(q) {
  switch (normalizeType(q)) {
    case "MULTICHOICE": // green
      return { bgFrom:"rgba(20,120,60,.62)", bgTo:"rgba(12,80,40,.58)", border:"rgba(72,221,124,.6)", pillFrom:"#1b5e32", pillTo:"#114822", dotFrom:"#c8ffd9", dotTo:"#49e58a" };
    case "MULTIANSWER": // ORANGE to match editor (#e67e22 / #f39c12)
      return {
        bgFrom: "rgba(230,126,34,0.62)",
        bgTo:   "rgba(210,110,24,0.58)",
        border: "rgba(243,156,18,0.60)",
        pillFrom: "#ab5e12",
        pillTo:   "#8b4a0e",
        dotFrom:  "#fff0d6",
        dotTo:    "#ffd08a"
      };
    case "BETANSWER": // amber
      return { bgFrom:"rgba(160,110,0,.62)", bgTo:"rgba(110,70,0,.58)", border:"rgba(255,208,92,.6)", pillFrom:"#6c4a0a", pillTo:"#513805", dotFrom:"#ffe9b0", dotTo:"#ffc64d" };
    case "TYPE": // purple
      return { bgFrom:"rgba(90,35,140,.60)", bgTo:"rgba(60,22,95,.56)", border:"rgba(202,170,255,.6)", pillFrom:"#4f267e", pillTo:"#3b1a61", dotFrom:"#e8d7ff", dotTo:"#c09bff" };
    case "BREADCRUMBS": // brown/tan
      return { bgFrom:"rgba(141,110,99,.62)", bgTo:"rgba(109,76,65,.58)", border:"rgba(161,136,127,.6)", pillFrom:"#5d4037", pillTo:"#4e342e", dotFrom:"#d7ccc8", dotTo:"#a1887f" };
    case "NEARESTANSWER": // deep purple
      return { bgFrom:"rgba(110,35,170,.64)", bgTo:"rgba(72,18,120,.58)", border:"rgba(210,170,255,.68)", pillFrom:"#5b2396", pillTo:"#3e1670", dotFrom:"#f0e3ff", dotTo:"#bf98ff" };
    case "WHEEL": // indigo
      return { bgFrom:"rgba(45,56,150,.60)", bgTo:"rgba(28,34,100,.56)", border:"rgba(162,172,255,.6)", pillFrom:"#303c92", pillTo:"#242d72", dotFrom:"#dadaff", dotTo:"#a8afff" };
    case "TRUE/FALSE": // LEMON to match editor (#f4d03f / #f7dc6f)
      return {
        bgFrom: "rgba(244,208,63,0.62)",
        bgTo:   "rgba(223,186,45,0.58)",
        border: "rgba(247,220,111,0.60)",
        pillFrom: "#6a5605",
        pillTo:   "#524203",
        dotFrom:  "#fff5b0",
        dotTo:    "#ffe566"
      };
    case "OUTOFORDER": // apricot (match editor palette)
      return {
        bgFrom: "rgba(255, 200, 162, 0.62)",
        bgTo:   "rgba(255, 170, 120, 0.58)",
        border: "rgba(255, 218, 185, 0.60)",
        pillFrom: "#e6a97f",
        pillTo:   "#d68f63",
        dotFrom:  "#fff0e6",
        dotTo:    "#ffd1b5"
      };
    default: // fallback navy
      return { bgFrom:"rgba(16,22,36,.58)", bgTo:"rgba(10,14,24,.54)", border:"rgba(128,168,255,.5)", pillFrom:"#2a3f6a", pillTo:"#1b2b4d", dotFrom:"#d4e3ff", dotTo:"#7aa2ff" };
  }
}

function getTypeDisplayName(q) {
  const type = String(q?.type || q?.CAT || "").toUpperCase();
  switch (type) {
    case "MULTICHOICE":     return "Multiple Choice";
    case "MULTIANSWER":     return "Multiple Answer";
    case "TRUE/FALSE":      return "True/False";
    case "TRUEFALSE":       return "True/False";
    case "NEARESTANSWER":   return "Nearest Answer";
    case "TYPE":            return "Type Answer";
    case "BREADCRUMBS":     return "Breadcrumbs";
    case "BETANSWER":       return "Bet Answer";
    case "WHEEL":           return "Wheel";
    case "OUTOFORDER":      return "Out of Order";
    case "VANISHINGCHOICE": return "Vanishing Choice";
    default:                return "Question";
  }
}

function countCorrectOptions(q) {
  const ans = Array.isArray(q?.answers) ? q.answers : [];
  let count = 0;

  for (const a of ans) {
    if (!a) continue;
    const str = String(a).trim();
    // Check for [brackets], ✓, or (correct) markers
    if (/^\s*\[.*?\]\s*$/.test(str) || str.endsWith("✓") || /\(correct\)/i.test(str)) {
      count++;
    } else if (typeof a === "object" && (a.isCorrect || a.correct)) {
      count++;
    }
  }
  return count;
}

function getRulesForQuestion(q) {
  // Prefer CSV-provided rules if present
  const rawCustom = q?.RULES_TEXT || q?.RULES || q?.rules_text || q?.rules || "";
  if (rawCustom && String(rawCustom).trim()) {
    return String(rawCustom)
      .replace(/\{LB\}/gi, "\n")
      .split(/\r?\n/)
      .map(s => s.trim())
      .filter(Boolean)
      .slice(0, 6);
  }

  const t = normalizeType(q);
  const ans = Array.isArray(q?.answers) ? q.answers : [];
  const multiPickCount =
    ans.filter(a => /^\s*\[.*?\]\s*$/.test(String(a ?? ""))).length;

  switch (t) {
    case "MULTICHOICE":
      return [
        "Select one answer",
        "No penalty points or bonus points"
      ];

    case "VANISHINGCHOICE":
      return [
        "Wrong answers vanish over time",
        "DO NOT GUESS! Penalty points apply"
      ];

    case "OUTOFORDER":
      return [
        "Drag the items into the correct sequence",
        "Points are awarded for each item in the correct position"
        ];

    case "MULTIANSWER":
      return [
        multiPickCount > 1 ? `Select ${multiPickCount} answers` : "Select multiple answers",
        "Select multiple answers for each question",
        "Each correct answer is rewarded"
      ];
    case "BETANSWER":
      return [
        "Place a bet before answering",
        "Correct answer wins your bet, wrong loses it",
        "Bet smart for maximum return"
      ];
    case "TYPE":
      return [
        "You're allowed the odd typo!",
        "More points for faster answers"
      ];
    case "NEARESTANSWER":
      return [
        "Guess a number closest to the answer",
        "Nearest 5 teams score points",
        "Fastest answers break ties"
      ];
    case "JACKPOT":
      return [
        "Guess a number closest to the answer",
        "No points - scored manually by host",
        "Players can send representatives to the front"
      ];
    case "WHEEL":
      return [
        "Letters reveal over time",
        "Guess the answer as letters appear"
      ];
    case "TRUE/FALSE":
    case "TRUEFALSE":
      return [
        "Select True or False",
        "Simple binary choice"
      ];
    default:
      return ["Follow the instructions", "Answer carefully"];
  }
}

function RulesSlideInCard({ q, lines = [], visible = false, questionNumber = 0 }) {
  const theme = getTypeTheme(q);

  // Present mixed-case labels for type
  const toTitleCase = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  // Points display
  const rawPoints =
    q?.POINTS ?? q?.points ?? q?.MaxPoints ?? q?.max_points ?? q?.max ?? "";
  const basePoints = Number.parseInt(String(rawPoints).trim(), 10);
  const hasBasePoints = Number.isFinite(basePoints) && basePoints > 0;

  let pointsText = "";
  if (hasBasePoints) {
    const t = normalizeType(q);

    if (t === "MULTIANSWER") {
      pointsText = `${basePoints} points per correct`;
    }
    else if (t === "MULTICHOICE") {
      const correctCount = countCorrectOptions(q);
      if (correctCount > 0) {
        const total = basePoints * correctCount;
        pointsText = `${basePoints} pts × ${correctCount} correct = ${total} pts max`;
      } else {
        pointsText = `${basePoints} pts available`;
      }
    }
    else if (t === "BETANSWER") {
      pointsText = `Up to ${basePoints} pts (your bet)`;
    } else {
      pointsText = `${basePoints} pts max`;
    }
  }

  const typeLabelMixedCase = toTitleCase(getTypeDisplayName(q));

  // Animation + base container
  const showTransition = "transform 560ms cubic-bezier(.2,.7,.2,1), opacity 360ms ease";
  const hideTransition =
    "transform 2200ms cubic-bezier(.16,.74,.12,1), opacity 1600ms ease 250ms";

  const base = {
    position: "fixed",
    right: "2vw",
    bottom: "1vh", // Reduced bottom margin to give more space
    width: "clamp(280px, 22vw, 400px)", // Slightly wider to take up more space
    minWidth: 280,
    maxWidth: "90vw", // Prevent overflow on very narrow screens
    height: "auto", // Let content determine height
    maxHeight: "calc(100vh - 8vh)", // Leave room for top margin and bottom elements (timer bar is at 5vh)
    minHeight: 280, // Increased minimum height
    padding: "16px 18px",
    borderRadius: 16,
    color: "#f3f6ff",
    background: `linear-gradient(180deg, ${theme.bgFrom}, ${theme.bgTo})`,
    border: `1px solid ${theme.border}`,
    boxShadow: "0 10px 44px rgba(0,0,0,.55), inset 0 1px 1px rgba(255,255,255,.08)",
    transform: visible ? "translateY(0) scale(1)" : "translateY(120%) scale(.96)",
    opacity: visible ? 1 : 0,
    transition: visible ? showTransition : hideTransition,
    zIndex: 99999, // Very high z-index to ensure it's above all other elements
    pointerEvents: "none",
    display: "grid",
    gridTemplateRows: "auto auto auto 1fr",
    rowGap: 10,
    whiteSpace: "normal",
    overflow: "hidden",
    willChange: "transform, opacity"
  };

  // Shared pill styling (mixed case)
  const pillBase = {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    fontWeight: 900,
    letterSpacing: ".06em",
    textTransform: "none",
    color: "#eaf3ff",
    background: `linear-gradient(180deg, ${theme.pillFrom}, ${theme.pillTo})`,
    border: `2px solid ${theme.border}`,
    borderRadius: 999,
    padding: "9px 14px",
    textShadow: "0 2px 10px #001a",
    boxShadow:
      "inset 0 2px 4px rgba(255,255,255,.10), 0 6px 16px rgba(0,0,0,.35), 0 0 22px rgba(0,0,0,.15)"
  };

  // First three rows: slightly smaller, single line only
  const pillSmallNowrap = {
    ...pillBase,
    fontSize: "clamp(12px, 1.1vw, 17px)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  };

  // Rules list container: can wrap (no scrollbars on panel)
  const ul = {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "grid",
    gap: "8px",
    fontWeight: 800,
    fontSize: "clamp(14px, 1.1vw, 18px)",
    lineHeight: 1.25,
    overflow: "hidden",
    minHeight: 0, // Allow shrinking if needed
    maxHeight: "100%", // Ensure it doesn't overflow parent
    alignContent: "start" // Align content to top if it doesn't fill space
  };

  const li = {
    display: "grid",
    gridTemplateColumns: "18px 1fr",
    alignItems: "start",
    gap: "10px",
    background: "linear-gradient(180deg, rgba(255,255,255,.06), rgba(255,255,255,0))",
    border: `1px solid ${theme.border}`,
    borderRadius: 12,
    padding: "8px 10px",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,.06)"
  };

  const dot = {
    width: 12,
    height: 12,
    marginTop: 4,
    borderRadius: 999,
    background: `radial-gradient(circle at 30% 30%, ${theme.dotFrom}, ${theme.dotTo})`
  };

  return (
    <div style={base}>
      {/* 1) Question number — smaller, single line */}
      {questionNumber > 0 && (
        <div style={pillSmallNowrap}>{`Question ${questionNumber}`}</div>
      )}

      {/* 2) Question type — smaller, mixed case, single line (no bullet) */}
      <div style={pillSmallNowrap}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          {typeLabelMixedCase}
        </span>
      </div>

      {/* 3) Points — smaller, mixed case, single line */}
      {pointsText && (
        <div style={pillSmallNowrap}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {pointsText}
          </span>
        </div>
      )}

      {/* 4) Rules list — wraps normally */}
      <ul style={ul}>
        {lines.slice(0, 6).map((t, i) => (
          <li key={i} style={li}>
            <span style={dot} />
            <span
              style={{
                textShadow: "0 1px 6px #0008",
                whiteSpace: "normal",
                overflow: "visible",
                textOverflow: "clip",
                display: "block"
              }}
            >
              {t}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PointsCoin({ value, ready = false, closing = false }) {
  const showNumber = typeof value === "number";
  const plus = showNumber && value > 0 ? "+" : "";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "grid",
        placeItems: "center",
        zIndex: 5000,
        pointerEvents: "none",
        animation: closing ? "pcFadeOut .35s ease forwards" : "pcFadeIn .18s ease-out",
      }}
    >
      <div
        /* Coin container */
        style={{
          minWidth: 220,
          minHeight: 220,
          width: "34vw",
          height: "34vw",
          maxWidth: 340,
          maxHeight: 340,
          borderRadius: "999px",
          background:
            "radial-gradient(70% 70% at 50% 40%, #ffe794 0%, #ffcc3b 48%, #d69f1a 70%, #b77e0b 100%)",
          boxShadow:
            "0 18px 60px rgba(0,0,0,.40), inset 0 3px 8px rgba(255,255,255,.6), inset 0 -8px 14px rgba(0,0,0,.35), 0 0 44px rgba(255,215,0,.35)",
          display: "grid",
          placeItems: "center",
          position: "relative",
          transformOrigin: "50% 50%",
          opacity: 0.88,
          /* Pop-in on mount; then ping when ready flips true */
          animation: ready
            ? "pcPing 280ms cubic-bezier(.2,.8,.25,1) 1"
            : "pcPopIn 450ms cubic-bezier(.2,.8,.25,1) 1",
        }}
      >
        {/* outer ring */}
        <div
          style={{
            position: "absolute",
            inset: 10,
            borderRadius: "999px",
            border: "8px solid rgba(255,255,255,.45)",
            boxShadow:
              "inset 0 2px 8px rgba(0,0,0,.25), inset 0 -4px 10px rgba(0,0,0,.25)",
          }}
        />

        {/* Sheen while waiting (subtle sweep) */}
        {!ready && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: "-30%",
              width: "60%",
              height: "100%",
              transform: "skewX(-20deg)",
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,.35) 45%, rgba(255,255,255,.0) 60%)",
              filter: "blur(1px)",
              animation: "pcSheen 1.2s ease-in-out infinite",
              pointerEvents: "none",
              borderRadius: "999px",
            }}
          />
        )}

        {/* Number (fades in on ready) */}
        <div
          style={{
            position: "relative",
            fontWeight: 900,
            fontFamily: "'Montserrat', system-ui, Arial, Helvetica, sans-serif",
            color: "#3a2200",
            textShadow:
              "0 3px 0 #ffd76a, 0 8px 24px rgba(0,0,0,.35), 0 0 12px rgba(255,220,80,.35)",
            fontSize: "clamp(2.1rem, 9vw, 4.2rem)",
            letterSpacing: ".02em",
            opacity: showNumber && ready ? 1 : 0,
            transition: "opacity 180ms ease",
          }}
        >
          {plus}
          {value}
          <span style={{ fontSize: "60%", marginLeft: 8 }}>pts</span>
        </div>
      </div>

      {/* keyframes inline so no stylesheet edits needed */}
      <style>{`
        @keyframes pcFadeIn {
          from { opacity: 0; transform: scale(.95); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes pcFadeOut {
          from { opacity: 1; transform: scale(1); }
          to   { opacity: 0; transform: scale(.98); }
        }
        /* Pop-in: 0.85 -> 1.08 -> 1.00 */
        @keyframes pcPopIn {
          0%   { transform: scale(0.85); }
          70%  { transform: scale(1.08); }
          100% { transform: scale(1.00); }
        }
        /* Ping on ready: 1.00 -> 1.05 -> 1.00 */
        @keyframes pcPing {
          0%   { transform: scale(1.00); }
          60%  { transform: scale(1.05); }
          100% { transform: scale(1.00); }
        }
        /* Sheen sweep while waiting */
        @keyframes pcSheen {
          0%   { transform: translateX(0) skewX(-20deg); opacity: 0; }
          12%  { opacity: .55; }
          60%  { transform: translateX(220%) skewX(-20deg); opacity: 0; }
          100% { transform: translateX(220%) skewX(-20deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}






// Player.js — NEW: wrapper that shows children only when open

/// Covers the question until the big screen shows it (hosts write `questionShownQ` when the
/// question appears on the TV), so phones never get ahead of the room.
function GetReadyCard({ q, number }) {
  const label = getTypeDisplayName(q) || "";
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(10, 12, 20, 0.97)", display: "flex",
      flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 18, padding: "6vw", textAlign: "center",
      fontFamily: "'Arial Black', Impact, Arial, sans-serif", color: "#fff" }}>
      <div style={{ fontSize: "clamp(1.1rem, 4.5vw, 1.6rem)", opacity: 0.8 }}>{number > 0 ? `Question ${number}` : "Next question"}</div>
      <div style={{ fontSize: "clamp(1.8rem, 8vw, 3rem)", lineHeight: 1.15 }}>{label || "Get ready"}</div>
      <div style={{ fontSize: "clamp(1rem, 4vw, 1.3rem)", opacity: 0.75, fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Eyes on the big screen…</div>
    </div>
  );
}

const ordinalWord = (n) => { const s = ["th", "st", "nd", "rd"], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };

/// End of the quiz: this team's points and position for every round, where it finished overall,
/// and a way back to the home screen.
function EndOfQuizResults({ gameCode, game, team }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        // Native app: the Firestore SDK never responds in the WebView, so read via REST.
        const data = typeof window !== "undefined" && window.Capacitor
          ? (await fetchAnswersViaRest(gameCode)).map((r) => r.data)
          : (await getDocs(collection(db, "games", gameCode, "answers"))).docs.map((d) => d.data());
        if (live) setRows(data);
      } catch (e) {
        if (live) setRows([]);
      }
    })();
    return () => { live = false; };
  }, [gameCode]);
  const summary = useMemo(() => {
    if (!rows || !Array.isArray(game?.questions) || !team) return null;
    const qs = game.questions;
    const rounds = [];
    let start = null;
    qs.forEach((q, i) => {
      const t = String(q?.CAT || q?.type || "").toUpperCase();
      if (t === "STARTROUND") start = i;
      else if (t === "ENDROUND" && start != null) { rounds.push({ start, end: i }); start = null; }
    });
    if (!rounds.length) rounds.push({ start: 0, end: qs.length });
    const teams = [...new Set(rows.map((r) => r.team).filter(Boolean))];
    const pts = (t, s, e) => rows.filter((r) => r.team === t && typeof r.q === "number" && r.q >= s && r.q < e)
      .reduce((a, r) => a + (Number(r.points) || 0), 0);
    const position = (scores, t) => {
      const mine = scores.find((x) => x.team === t)?.points ?? 0;
      return scores.filter((x) => x.points > mine).length + 1;
    };
    const perRound = rounds.map((r, i) => {
      const scores = teams.map((t) => ({ team: t, points: pts(t, r.start, r.end) }));
      return { name: `Round ${i + 1}`, points: pts(team, r.start, r.end), position: position(scores, team) };
    });
    const totals = teams.map((t) => ({ team: t, points: pts(t, 0, qs.length) }));
    return { perRound, total: pts(team, 0, qs.length), finalPos: position(totals, team), teams: teams.length };
  }, [rows, game, team]);
  const gold = "linear-gradient(180deg, #fff7c2, #f2c94c)";
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 70, overflowY: "auto", background: "rgba(10, 12, 20, 0.96)", color: "#fff",
      fontFamily: "'Arial Black', Impact, Arial, sans-serif", padding: "max(4vh, env(safe-area-inset-top)) 5vw 5vh", boxSizing: "border-box" }}>
      <div style={{ textAlign: "center", fontSize: "clamp(1.4rem, 6vw, 2.2rem)", marginBottom: 8 }}>Final results</div>
      <div style={{ textAlign: "center", fontSize: "clamp(1rem, 4vw, 1.3rem)", opacity: 0.85, marginBottom: 18 }}>{team}</div>
      {!summary ? (
        <div style={{ textAlign: "center", opacity: 0.8 }}>Adding up the scores…</div>
      ) : (
        <>
          <div style={{ margin: "0 auto 18px", width: "min(420px, 90vw)", borderRadius: 18, padding: "14px 16px", background: gold, color: "#4b3b00", textAlign: "center" }}>
            <div style={{ fontSize: "clamp(0.9rem, 3.5vw, 1.1rem)" }}>You finished</div>
            <div style={{ fontSize: "clamp(2.4rem, 12vw, 4rem)", lineHeight: 1 }}>{ordinalWord(summary.finalPos)}</div>
            <div style={{ fontSize: "clamp(0.9rem, 3.5vw, 1.1rem)" }}>of {summary.teams} teams · {summary.total} points</div>
          </div>
          <table style={{ width: "min(420px, 90vw)", margin: "0 auto", borderCollapse: "separate", borderSpacing: "0 6px", fontSize: "clamp(0.95rem, 4vw, 1.2rem)" }}>
            <thead><tr style={{ opacity: 0.7, fontSize: "0.8em" }}><th style={{ textAlign: "left", padding: "0 10px" }}>Round</th><th style={{ padding: "0 10px" }}>Points</th><th style={{ padding: "0 10px" }}>Position</th></tr></thead>
            <tbody>
              {summary.perRound.map((r) => (
                <tr key={r.name} style={{ background: "rgba(255,255,255,0.08)" }}>
                  <td style={{ padding: "10px", borderRadius: "12px 0 0 12px" }}>{r.name}</td>
                  <td style={{ padding: "10px", textAlign: "center" }}>{r.points}</td>
                  <td style={{ padding: "10px", textAlign: "center", borderRadius: "0 12px 12px 0" }}>{ordinalWord(r.position)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <div style={{ textAlign: "center", marginTop: 26 }}>
        <button type="button" onClick={() => { window.location.href = "/play/"; }}
          style={{ fontFamily: "inherit", fontSize: "clamp(1rem, 4.5vw, 1.3rem)", padding: "14px 32px", borderRadius: 999, border: "none",
            background: "linear-gradient(180deg, #22c55e, #15803d)", color: "#fff", boxShadow: "0 6px 18px rgba(0,0,0,0.45)" }}>
          Back to home
        </button>
      </div>
    </div>
  );
}

function AnswersGate({ open, children }) {
  if (!open) return null;
  return <>{children}</>;
}

export default function Player() {
    // --- All useState hooks go first ---
    const [game, setGame] = useState(null);
    const [current, setCurrent] = useState(0);
    const [timer, setTimer] = useState(0);
    const [wizardStep, setWizardStep] = useState(0);
    const [team, setTeam] = useState("");
    const [selected, setSelected] = useState([]);
    const [numberAnswer, setNumberAnswer] = useState("");
    const [answered, setAnswered] = useState(false);
    const [selfieUrl, setSelfieUrl] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const [selfieFile, setSelfieFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [myPointsThisQuestion, setMyPointsThisQuestion] = useState(null);
    const [showPointsPopup, setShowPointsPopup] = useState(false);
    const [pointsReady, setPointsReady] = useState(false);
    const [popupClosing, setPopupClosing] = useState(false);
    const [vanishingIndexes, setVanishingIndexes] = useState([]);
    const [vanishingPlan, setVanishingPlan] = useState(null);
    const [betAmount, setBetAmount] = useState(0);
    const [betSubmitted, setBetSubmitted] = useState(false);
    const [answersOpen, setAnswersOpen] = useState(false);
    const [oooColorSlot, setOooColorSlot] = useState(null);
    const [orderedAnswers, setOrderedAnswers] = useState([]);
    const [orderAtReveal, setOrderAtReveal] = useState(null);
    const { gameCode } = useParams();
    const MAX_TEAM_NAME_LENGTH = "The Mighty Morphin Flower Arrangers society".length;
    const [nameValidationFeedback, setNameValidationFeedback] = useState("");
    const [restoring, setRestoring] = useState(false);
    const [rulesCardVisible, setRulesCardVisible] = useState(false);
    const [timerActive, setTimerActive] = useState(false); // Track if timer is currently active
    const restoredAnswersRef = useRef(new Map()); // question index -> { answered, selected, etc }
    const oooInitializedRef = useRef(null); // Track which question we've initialized OUTOFORDER for
    const rulesCardTimerRef = useRef({ show: 0, hide: 0 }); // Track rules card timers
    const lastQuestionRef = useRef(null); // Track the last question index we set up timers for
    const timerIntervalRef = useRef(null); // Reference for local countdown interval
    const prevCurrentRef = useRef(0); // Track previous current to detect question changes
    const prevTimerActiveRef = useRef(false); // Track previous timerActive to detect changes
    // --- All other hooks (useMemo, useRef, useEffect) come after useState ---
    const qMemo = useMemo(() => (game?.questions?.[current]) || {}, [game, current]);

    const displayedAnswersMemo = useMemo(() => {
      const ans = qMemo?.answers || [];
      const order = qMemo?.answersOrder;
      return Array.isArray(order) ? order.map(i => ans[i]) : ans;
    }, [qMemo]);

    const shouldShowTimer = useMemo(() => {
        if (!qMemo) return false;
        const rawType = String(qMemo.type || qMemo.CAT || "").trim().toUpperCase();
        const displayAliases = new Set([
            "DISPLAY", "SLIDE", "DEFAULT", "STARTROUND", "ENDROUND", "SHOWROUNDS",
            "INTRO", "INTRODUCTION", "JOIN QUIZ", "END QUIZ", "BREAK", "BREAK TIME"
        ]);
        // Don't show timer for display slides or when timer is 0 and inactive (question ended)
        if (displayAliases.has(rawType)) return false;
        if (timer === 0 && !timerActive) return false; // Hide timer after question ends
        if (shouldDeferTimerUntilAnswersGate(qMemo)) {
          if (!answersOpen || !timerActive) return false;
        }
        return true;
    }, [qMemo, timer, timerActive, answersOpen]);

    const inputRef = useRef();
    const pointsPopupTO = useRef(0);
    const questionOpenedAtRef = useRef(0);
    const handleSubmitRef = useRef(null); // For auto-submit effect



//********************* */


  // Detect if running as PWA standalone/fullscreen
const isStandalone = window.matchMedia('(display-mode: standalone)').matches
                || window.navigator.standalone === true;

                 // NEW: boundary for "this run"


// Player.js — helper: which slides can award points?
const isScoringSlide = (q) => {
  const t = String(q?.type || q?.CAT || "").toUpperCase();
  return [
    "MULTICHOICE",
    "TRUE/FALSE",
    "MULTIANSWER",
    "VANISHINGCHOICE",
    "NEARESTANSWER",
    "WHEEL",
    "TYPE",
    "BREADCRUMBS",
    "BETANSWER",
    "OUTOFORDER",
    "BUZZER",
  ].includes(t);
};


// 👇 ADD THIS useMemo HOOK 👇
const displayQuestionNumber = useMemo(() => {
  if (!Array.isArray(game?.questions) || current < 0) return 0;
  let count = 0;
  for (let i = 0; i <= current; i++) {
    const it = game.questions[i] || {};
    if (normalizeType(it) !== "DISPLAY") count++;
  }
  return count;
}, [game?.questions, current]);


useEffect(() => {
  questionOpenedAtRef.current = Date.now();       // NEW: start-of-run boundary
  setOrderAtReveal(null);
  setPointsReady(false);
  setMyPointsThisQuestion(null);
  setPopupClosing(false);
  setShowPointsPopup(false); // Hide score badge when question changes
  setAnswered(false);  // Reset answered state when question changes
  setSelected([]);      // Reset selected answers
  setNumberAnswer(""); // Reset text input
  setFeedback(null);    // Reset feedback
}, [current]);

// Separate effect for rules card - only runs when current question index changes
useEffect(() => {
  // Only set up rules card if this is a new question (not the same one we already set up)
  if (lastQuestionRef.current === current) {
    return; // Already set up for this question
  }
  
  // Clear any existing timers
  if (rulesCardTimerRef.current.show) {
    clearTimeout(rulesCardTimerRef.current.show);
    rulesCardTimerRef.current.show = 0;
  }
  if (rulesCardTimerRef.current.hide) {
    clearTimeout(rulesCardTimerRef.current.hide);
    rulesCardTimerRef.current.hide = 0;
  }
  
  // Reset visibility
  setRulesCardVisible(false);
  
  // Mark that we've set up for this question
  lastQuestionRef.current = current;
  
  // Show rules card after 700ms, hide after 7000ms on player
  // Only set up timers if we have a question and it's not a display slide
  const currentQuestion = game?.questions?.[current];
  if (currentQuestion && normalizeType(currentQuestion) !== "DISPLAY") {
    rulesCardTimerRef.current.show = setTimeout(() => setRulesCardVisible(true), 700);
    rulesCardTimerRef.current.hide = setTimeout(() => setRulesCardVisible(false), 7000);
  }
  
  return () => {
    if (rulesCardTimerRef.current.show) clearTimeout(rulesCardTimerRef.current.show);
    if (rulesCardTimerRef.current.hide) clearTimeout(rulesCardTimerRef.current.hide);
  };
}, [current]); // Only depend on current, check game inside





useEffect(() => {
  if (!gameCode || !team) return;
  const applyAnswerData = (data) => {
    if (!data) {
      setMyPointsThisQuestion(null);
      setPointsReady(false);
      return;
    }

    // Use server time if present, else fall back to client time
    const submittedAtMs =
      firestoreTimestampToMs(data.submittedAt) || (typeof data.time === "number" ? data.time : 0);

    // Accept points only if this submission happened AFTER this question opened
    const freshForThisRun = submittedAtMs >= (questionOpenedAtRef.current || 0);

    // Optional extra safety if your scorer writes it:
    // const scored = data.awarded === true;

    if (freshForThisRun && typeof data.points === "number" /* && scored */) {
      setMyPointsThisQuestion(data.points);
      setPointsReady(true);
    } else {
      // Either not scored yet, or old/stale doc from a previous run — keep waiting
      setMyPointsThisQuestion(null);
      setPointsReady(false);
    }
  };

  if (typeof window !== "undefined" && window.Capacitor) {
    let cancelled = false;
    let pollId = 0;
    const answerPath = `games/${encodeURIComponent(gameCode)}/answers/${encodeURIComponent(`${current}-${team}`)}`;
    const poll = async () => {
      try {
        const data = await fetchFirestoreDocViaRest(answerPath);
        if (!cancelled) applyAnswerData(data);
      } catch (err) {
        console.warn("Player answer points REST poll failed:", err);
      }
    };
    poll();
    pollId = setInterval(poll, 1500);
    return () => {
      cancelled = true;
      if (pollId) clearInterval(pollId);
    };
  }

  const answerRef = doc(db, "games", gameCode, "answers", `${current}-${team}`);
  const unsub = onSnapshot(answerRef, (snap) => {
    applyAnswerData(snap.exists() ? snap.data() : null);
  });
  return () => unsub();
}, [current, team, gameCode]);






// OUTOFORDER — only initialise once per question, same as MULTICHOICE (matches legacy)
useEffect(() => {
  const questionType = String(qMemo?.type || "").toUpperCase();
  const questionId = current; // Use current question index as unique ID
  
  if (questionType !== "OUTOFORDER") {
    setOrderedAnswers([]);
    setOooColorSlot(null);
    oooInitializedRef.current = null;
    return;
  }
  
  // Only initialize once per question (using ref to track)
  if (oooInitializedRef.current === questionId) return;
  
  // Reset when question changes
  if (oooInitializedRef.current !== null && oooInitializedRef.current !== questionId) {
    setOrderedAnswers([]);
    setOooColorSlot(null);
  }

  if (Array.isArray(qMemo?.answersOrder) && qMemo.answersOrder.length > 0) {
    const initOrder = qMemo.answersOrder.slice();
    setOrderedAnswers(initOrder);
    oooInitializedRef.current = questionId;

    // Build stable colour-slot map: answerIdx -> initial display slot (mod 6)
    const map = {};
    initOrder.forEach((answerIdx, displayIdx) => {
      map[answerIdx] = displayIdx % 6;
    });
    setOooColorSlot(map);

  } else if (Array.isArray(qMemo?.answers) && qMemo.answers.length > 0) {
    const initOrder = qMemo.answers.map((_, i) => i);
    setOrderedAnswers(initOrder);
    oooInitializedRef.current = questionId;

    const map = {};
    initOrder.forEach((answerIdx, displayIdx) => {
      map[answerIdx] = displayIdx % 6;
    });
    setOooColorSlot(map);
  }
}, [qMemo, current]); // Depend on qMemo and current to reset on question change


// When time runs out, show the correct sorted order for OUTOFORDER
// When time runs out: snapshot player's final arrangement (for grading), but DON'T reorder
useEffect(() => {
  if (String(qMemo?.type || "").toUpperCase() !== "OUTOFORDER") return;
  if (timer !== 0) return;

  setOrderAtReveal(prev => prev ?? orderedAnswers.slice());
}, [timer, qMemo, orderedAnswers]);


// A) Open/close coin strictly on timer end/start — ONLY for scoring slides
useEffect(() => {
  const scoring = isScoringSlide(qMemo);

  if (!scoring) {
    // Never show coin on non-scoring slides (INTRO/STARTROUND/ENDROUND/SHOWROUNDS/DISPLAY, etc.)
    setShowPointsPopup(false);
    setPopupClosing(false);
    if (pointsPopupTO.current) {
      clearTimeout(pointsPopupTO.current);
      pointsPopupTO.current = 0;
    }
    return;
  }

  if (!timerActive) {
    setPopupClosing(false);
    setShowPointsPopup(true);    // open at time-up for scoring slides only
  } else {
    // Timer is active, hide popup if showing
    setPopupClosing(false);
    setShowPointsPopup(false);
    if (pointsPopupTO.current) {
      clearTimeout(pointsPopupTO.current);
      pointsPopupTO.current = 0;
    }
  }

  return () => {
    if (pointsPopupTO.current) {
      clearTimeout(pointsPopupTO.current);
      pointsPopupTO.current = 0;
    }
  };
}, [timerActive, qMemo]);



// B) Auto-hide popup after 5 seconds when points are ready
useEffect(() => {
  // Ensure closing state is false when points are ready (for smooth display)
  if (pointsReady && showPointsPopup) {
    setPopupClosing(false);
    
    // Clear any existing timeout
    if (pointsPopupTO.current) {
      clearTimeout(pointsPopupTO.current);
      pointsPopupTO.current = 0;
    }
    
    // Set 5-second timeout to hide the popup
    pointsPopupTO.current = setTimeout(() => {
      setPopupClosing(true);
      // Hide after closing animation completes (0.35s based on pcFadeOut animation)
      setTimeout(() => {
        setShowPointsPopup(false);
        pointsPopupTO.current = 0;
      }, 350);
    }, 5000);
  }
  
  return () => {
    if (pointsPopupTO.current) {
      clearTimeout(pointsPopupTO.current);
      pointsPopupTO.current = 0;
    }
  };
}, [pointsReady, showPointsPopup]);






// Fetch my points for the question after timer ends (for display)
useEffect(() => {
  if (!gameCode || !team) return;
  if (typeof window !== "undefined" && window.Capacitor) return;
  const answerRef = doc(db, "games", gameCode, "answers", `${current}-${team}`);
  const unsub = onSnapshot(answerRef, (snap) => {
    if (snap.exists()) {
      const data = snap.data();
      if (typeof data.points === "number") {
        setMyPointsThisQuestion(data.points); // real value from host
        setPointsReady(true);
      } else {
        setMyPointsThisQuestion(null);        // not written yet
        setPointsReady(false);
      }
    } else {
      setMyPointsThisQuestion(null);          // no doc yet
      setPointsReady(false);
    }
  });
  return () => unsub();
}, [current, team, gameCode]);




  // Auto-restore from localStorage on mount
  useEffect(() => {
    if (!gameCode) return;
    const savedTeam = localStorage.getItem("player_team");
    const savedGameCode = localStorage.getItem("player_gameCode");
    const savedSelfieUrl = localStorage.getItem("player_selfieUrl");
    const savedWizardStep = localStorage.getItem("player_wizardStep");

    // Only auto-restore if saved gameCode matches current URL gameCode
    if (savedTeam && savedGameCode === gameCode && savedWizardStep === "2") {
      setRestoring(true);
      setTeam(savedTeam);
      if (savedSelfieUrl) setSelfieUrl(savedSelfieUrl);
      setWizardStep(2);

      // Fetch existing answers from Firebase to restore answered state
      (async () => {
        try {
          const isNativeApp = typeof window !== "undefined" && window.Capacitor;
          const [answerRows, gameData] = isNativeApp
            ? await Promise.all([
                fetchAnswersViaRest(gameCode),
                fetchGameDocumentViaRest(gameCode),
              ])
            : await Promise.all([
                getDocs(collection(db, "games", gameCode, "answers")).then((snap) =>
                  snap.docs.map((docSnap) => ({ id: docSnap.id, data: docSnap.data() }))
                ),
                getDoc(doc(db, "games", gameCode)).then((snap) => snap.data() || {}),
              ]);
          const questions = gameData?.questions || [];
          const answeredMap = new Map();
          answerRows.forEach(({ data }) => {
            if (data.team === savedTeam && typeof data.q === "number") {
              const qMeta = questions[data.q] || {};
              const qType = String(qMeta?.type || qMeta?.CAT || "").toUpperCase();
              const answered = qType === "BREADCRUMBS" ? data.correct === true : true;
              const ansArr = Array.isArray(qMeta.answers) ? qMeta.answers : [];
              const order = getDisplayAnswerOrder({ ...qMeta, answers: ansArr });
              let selectedDisp = [];
              if (qType === "MULTIANSWER" && Array.isArray(data.answer)) {
                selectedDisp = data.answer
                  .map((canon) => order.indexOf(canon))
                  .filter((d) => d >= 0);
              } else if (
                typeof data.answer === "number" &&
                [
                  "MULTICHOICE",
                  "TRUE/FALSE",
                  "TRUEFALSE",
                  "BETANSWER",
                  "VANISHINGCHOICE",
                ].includes(qType)
              ) {
                const d = order.indexOf(data.answer);
                selectedDisp = d >= 0 ? [d] : [];
              } else if (Array.isArray(data.answer)) {
                selectedDisp = [...data.answer];
              } else if (typeof data.answer === "number") {
                selectedDisp = [data.answer];
              }
              answeredMap.set(data.q, {
                answered,
                answer: data.answer,
                points: data.points,
                selected: selectedDisp,
              });
            }
          });
          restoredAnswersRef.current = answeredMap;
          console.log("[Player] Restored answers for", answeredMap.size, "questions");
        } catch (err) {
          console.error("[Player] Failed to fetch existing answers:", err);
        } finally {
          setRestoring(false);
        }
      })();
    }
  }, [gameCode]);

  // Listen for live game state
const applyGameData = React.useCallback((data) => {
  if (!data) return;
  setGame(data);
  setCurrent(data?.current ?? 0);
  const qIdx = data?.current ?? 0;

  const gateQ = data?.answersGateQ ?? -1;
  setAnswersOpen(gateQ === qIdx);

  setVanishingPlan(data?.vanishingPlan ?? null);

  const qData = data?.questions?.[qIdx];
  const allIdx =
    Array.isArray(qData?.answersOrder)
      ? [...qData.answersOrder]
      : Array.isArray(qData?.answers)
        ? qData.answers.map((_, i) => i)
        : [];

  if (data?.vanishingPlan) {
    setVanishingIndexes(
      computeVanishingIndexesFromPlan(data.vanishingPlan, getSyncedRemainingSeconds(data, qData))
    );
  } else {
    setVanishingIndexes(
      Array.isArray(data?.vanishingIndexes) && data.vanishingIndexes.length
        ? data.vanishingIndexes
        : allIdx
    );
  }

  const newTimerActive = data?.timerActive ?? false;
  const isNativeApp = typeof window !== "undefined" && window.Capacitor;
  const gateJustOpened = newTimerActive && !prevTimerActiveRef.current;
  if (newTimerActive !== prevTimerActiveRef.current) {
    setTimerActive(newTimerActive);
    prevTimerActiveRef.current = newTimerActive;
  }

  if (isNativeApp) {
    // iOS suspends JS intervals in the background, so derive the timer from server time.
    setTimer(getSyncedRemainingSeconds(data, qData));
    prevCurrentRef.current = data?.current ?? 0;
  } else if (data?.current !== prevCurrentRef.current) {
    const timerToUse = getQuestionDurationSeconds(qData, data?.timer ?? 0);
    setTimer(timerToUse);
    prevCurrentRef.current = data?.current ?? 0;
  } else if (gateJustOpened) {
    setTimer(getQuestionDurationSeconds(qData, data?.timer ?? 0));
  }

  if (!newTimerActive && timerIntervalRef.current) {
    clearInterval(timerIntervalRef.current);
    timerIntervalRef.current = null;
  }
}, []);

// Listen for live game state. Capacitor/iOS gets an extra REST poll fallback because
// Firestore's WebChannel listener can stall inside WKWebView.
useEffect(() => {
  if (!gameCode) return;
  const unsub = onSnapshot(
    doc(db, "games", gameCode),
    (snap) => applyGameData(snap.data()),
    (err) => console.error("Player game snapshot failed:", err)
  );

  let cancelled = false;
  let pollId = 0;
  if (typeof window !== "undefined" && window.Capacitor) {
    const poll = async () => {
      try {
        const data = await fetchGameDocumentViaRest(gameCode);
        if (!cancelled) applyGameData(data);
      } catch (err) {
        console.warn("Player REST poll failed:", err);
      }
    };
    poll();
    pollId = setInterval(poll, 2000);
    const refreshOnResume = () => {
      if (document.visibilityState === "hidden") return;
      poll();
    };
    document.addEventListener("visibilitychange", refreshOnResume);
    window.addEventListener("focus", refreshOnResume);
    window.addEventListener("pageshow", refreshOnResume);

    return () => {
      cancelled = true;
      if (pollId) clearInterval(pollId);
      document.removeEventListener("visibilitychange", refreshOnResume);
      window.removeEventListener("focus", refreshOnResume);
      window.removeEventListener("pageshow", refreshOnResume);
      unsub();
    };
  }

  return () => {
    cancelled = true;
    if (pollId) clearInterval(pollId);
    unsub();
  };
}, [gameCode, applyGameData]);

  const [teamChoiceSession, setTeamChoiceSession] = useState(null);
  const [teamChoiceTick, setTeamChoiceTick] = useState(0);
  useEffect(() => {
    if (!gameCode) return undefined;
    // Native app: Firestore SDK listeners never deliver in the WebView, so poll via REST.
    if (typeof window !== "undefined" && window.Capacitor) {
      let cancelled = false;
      const poll = async () => {
        try {
          const d = await fetchFirestoreDocViaRest(
            `games/${encodeURIComponent(gameCode)}/roundTagChoice/session`
          );
          if (!cancelled) setTeamChoiceSession(d);
        } catch (err) {
          console.warn("Team round choice REST poll failed:", err);
        }
      };
      poll();
      const id = setInterval(poll, 1000);
      return () => {
        cancelled = true;
        clearInterval(id);
      };
    }
    const ref = doc(db, "games", gameCode, "roundTagChoice", "session");
    return onSnapshot(ref, (snap) => {
      setTeamChoiceSession(snap.exists() ? snap.data() : null);
    });
  }, [gameCode]);

  useEffect(() => {
    if (!teamChoiceSession || !["awaitingPick", "revealPause"].includes(teamChoiceSession.phase)) {
      return undefined;
    }
    const id = setInterval(() => setTeamChoiceTick((t) => t + 1), 250);
    return () => clearInterval(id);
  }, [
    teamChoiceSession?.phase,
    teamChoiceSession?.deadlineAtMs,
    teamChoiceSession?.revealUntilMs,
  ]);

  const submitTeamRoundTag = async (tag) => {
    if (!gameCode || !team) return;
    const ref = doc(db, "games", gameCode, "roundTagChoice", "session");
    const st = getTeamRoundChoiceSettingsFromGameData(game || {});
    const pauseSec =
      typeof st.revealPauseSec === "number" && !Number.isNaN(st.revealPauseSec)
        ? st.revealPauseSec
        : DEFAULT_TEAM_ROUND_REVEAL_PAUSE_SEC;
    const revealMs = pauseSec * 1000;
    // Native app: same checks as the transaction below, written via REST with an updateTime
    // precondition so a pick can't overwrite a session that changed meanwhile.
    if (typeof window !== "undefined" && window.Capacitor) {
      const path = `games/${encodeURIComponent(gameCode)}/roundTagChoice/session`;
      try {
        const latest = await fetchFirestoreDocWithTimeViaRest(path);
        const d = latest?.data || {};
        if (!latest || d.phase !== "awaitingPick" || d.chosenTag || d.eligibleTeam !== team) return;
        const map = { ...(d.chosenTagByStartRound || {}) };
        if (d.startRoundIdx != null) map[String(d.startRoundIdx)] = tag;
        await updateFirestoreFieldsViaRest(
          path,
          {
            chosenTag: tag,
            pickSource: "team",
            phase: "revealPause",
            chosenTagByStartRound: map,
            revealUntilMs: Date.now() + revealMs,
            version: (d.version || 0) + 1,
            updatedAt: new Date(),
          },
          { updateTime: latest.updateTime }
        );
        setTeamChoiceSession((prev) => ({ ...(prev || d), chosenTag: tag, phase: "revealPause" }));
      } catch (e) {
        console.warn("submitTeamRoundTag (REST)", e);
      }
      return;
    }
    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        const d = snap.data() || {};
        if (d.phase !== "awaitingPick" || d.chosenTag) return;
        if (d.eligibleTeam !== team) return;
        const map = { ...(d.chosenTagByStartRound || {}) };
        if (d.startRoundIdx != null) map[String(d.startRoundIdx)] = tag;
        tx.set(
          ref,
          {
            chosenTag: tag,
            pickSource: "team",
            phase: "revealPause",
            chosenTagByStartRound: map,
            revealUntilMs: Date.now() + revealMs,
            version: (d.version || 0) + 1,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      });
    } catch (e) {
      console.warn("submitTeamRoundTag", e);
    }
  };

// Re-check answersGate when question changes (handles race condition on mobile)
useEffect(() => {
  if (!gameCode || current === undefined) return;
  
  // When question changes, check gate status again after a short delay
  // This handles cases where answersGateQ update arrives after current update
  const checkGate = setTimeout(() => {
    getDoc(doc(db, "games", gameCode)).then((snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      const qIdx = data?.current ?? 0;
      const gateQ = data?.answersGateQ ?? -1;
      
      // If we're still on the same question and gate should be open, update it
      if (qIdx === current && gateQ === current) {
        setAnswersOpen(true);
      }
    }).catch((err) => {
      console.error("[Player] Failed to re-check answersGate:", err);
    });
  }, 500); // Check after 500ms delay
  
  return () => clearTimeout(checkGate);
}, [current, gameCode]);

// Local countdown useEffect - runs timer countdown locally when timerActive is true
useEffect(() => {
  if (!timerActive) {
    // Clear interval if timer is not active
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    return;
  }

  // Don't start countdown if timer is already 0 (question already ended)
  // This prevents restarting countdown after question ends
  if (timer <= 0) {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    return;
  }

  // Start local countdown interval - simple decrement like Quizmaster
  timerIntervalRef.current = setInterval(() => {
    setTimer((t) => {
      const next = t - 1;
      if (next <= 0) {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        return 0;
      }
      return next;
    });
  }, 1000);

  // Cleanup on unmount or dependency change
  return () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  };
}, [timerActive]); // Only depend on timerActive, not timer (to avoid restarting interval every second)

// Vanishing choice: derive visible options from shared plan + local countdown (matches host).
useEffect(() => {
  if (!vanishingPlan) return;
  setVanishingIndexes(computeVanishingIndexesFromPlan(vanishingPlan, timer));
}, [vanishingPlan, timer]);

useEffect(() => {
  setBetSubmitted(false);
  setBetAmount(0); // (Optional) reset bet amount to 0 for new question
}, [current]);

  useEffect(() => {
    // Don't restore answered state for OUTOFORDER - let users submit manually
    const questionType = String(qMemo?.type || "").toUpperCase();
    if (questionType === "OUTOFORDER") {
      setAnswered(false);
      setSelected([]);
      setNumberAnswer("");
      setFeedback(null);
      return;
    }

    // Check if we have a restored answer for this question
    const restored = restoredAnswersRef.current.get(current);
    if (restored) {
      setAnswered(restored.answered);
      if (restored.selected && restored.selected.length > 0) {
        // Map answer indices to display indices if needed
        setSelected(restored.selected);
      }
      if (typeof restored.answer === "string" || typeof restored.answer === "number") {
        setNumberAnswer(String(restored.answer));
      }
    } else {
      setAnswered(false);
      setSelected([]);
      setNumberAnswer("");
      setFeedback(null);
    }
    // `restoring` too: after a rejoin the saved answers arrive after this first ran for the current question.
  }, [current, qMemo?.type, restoring]);

  // Auto-submit when timer runs out - must be before any conditional returns
  // OUTOFORDER auto-submits when timer runs out, NEARESTANSWER excluded (users need to interact)
  useEffect(() => {
    if (!game || !team || !qMemo || !handleSubmitRef.current) return;
    // Never blank-submit while a rejoin is still restoring saved answers (would overwrite the real answer),
    // nor for a question the restore found already answered: `answered` is set by another effect in this
    // same render, so it can still read false here when the timer had already run out before the rejoin.
    if (restoring) return;
    if (restoredAnswersRef.current.get(current)?.answered) return;
    const t = String(qMemo?.type || "").toUpperCase();
    // Not while the host is still picking a library question for this slide (the slide index
    // arrives before the question does, with the old timer), and never for an empty placeholder.
    const placeholder = game.selectionStatus === "selecting" ||
      (String(qMemo?.Q || qMemo?.question || "").trim().startsWith("#") && !(Array.isArray(qMemo?.answers) && qMemo.answers.length));
    if (placeholder) return;
    if (timer === 0 && !answered && t !== "NEARESTANSWER" && t !== "JACKPOT" && t !== "BREADCRUMBS") {
      handleSubmitRef.current();
    }
    // eslint-disable-next-line
  }, [timer, qMemo, answered, team, current, game, restoring]);
  
  if (!game) {
    return (
      <div style={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        background: "transparent",
        color: "#fff",
        padding: "24px",
        overflow: "auto",
        boxSizing: "border-box",
      }}>
        <div style={getPlayerQuestionPillStyle(false, { isFirst: true })}>
          {WAITING_FOR_QUIZMASTER_TEXT}
        </div>
        {shouldShowPhoneSetupTips() && <PhoneSetupTipsCard />}
      </div>
    );
  }

  const q = game.questions[current];
  
  // Check if JACKPOT is paused - show same screen as host (only if timer > 0)
  // After timer === 0, show normal question/answer interface even if paused
  const isJackpot = String(q?.type || q?.CAT || "").toUpperCase() === "JACKPOT";
  const isPaused = (() => {
    try {
      return (localStorage.getItem("opt_autoplay") || "Yes").toUpperCase() !== "YES";
    } catch {
      return false;
    }
  })();
  
  if (isJackpot && isPaused && timer > 0) {
    return (
      <div style={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(180deg, rgba(212,175,55,0.8), rgba(180,145,35,0.85))",
        color: "#111",
        fontFamily: "'Open Sans', Arial, sans-serif",
        flexDirection: "column",
        gap: "2vh"
      }}>
        <div style={{
          fontSize: "clamp(32px, 5vw, 64px)",
          fontWeight: 900,
          textAlign: "center",
          textShadow: "2px 2px 4px rgba(0,0,0,0.3)"
        }}>
          JACKPOT Question
        </div>
        <div style={{
          fontSize: "clamp(16px, 2vw, 28px)",
          fontWeight: 600,
          textAlign: "center",
          opacity: 0.85,
          marginTop: "1vh",
          maxWidth: "90vw"
        }}>
          Please send one representative from your team to the front to play the Jackpot question!
        </div>
      </div>
    );
  }
  const isBlock = q.question.startsWith("{B}");
  const questionDisplayText = getPlayerQuestionDisplayText(q, isBlock);
  const showLobbyWaiting = !answersOpen && isLobbyQuestion(current, q);
  let correctIndexes = [];

  if (q.type === "MULTICHOICE" || q.type === "TRUE/FALSE") {
    correctIndexes = [getDisplayAnswerOrder(q).findIndex((i) => i === 0)];
  } else if (q.type === "MULTIANSWER") {
    correctIndexes = (
      q.answersOrder
        ? q.answersOrder.map((origIdx, displayIdx) =>
            /^\[.*\]$/.test(game.questions[current].answers[origIdx])
              ? displayIdx
              : null
          )
        : q.answers.map((ans, i) => (/^\[.*\]$/.test(ans) ? i : null))
    ).filter((i) => i !== null);
  }

  const maxSelectable = q.maxSelectable || 1;
  const displayedAnswers = q.answersOrder
    ? q.answersOrder.map((i) => q.answers[i])
    : q.answers;
  const correctShuffledIndex = q.answersOrder
    ? q.answersOrder.findIndex((i) => i === 0)
    : 0;
  const isMulti = q.type === "MULTIANSWER";


// Drag handler for OUTOFORDER (we move indices)
const submitAnswerDoc = async (payload) => {
  const withTime = {
    ...payload,
    submittedAt: new Date(),
    time: payload.time || Date.now(),
  };
  if (typeof window !== "undefined" && window.Capacitor) {
    await setFirestoreDocViaRest(
      `games/${encodeURIComponent(gameCode)}/answers/${encodeURIComponent(`${current}-${team}`)}`,
      withTime
    );
    return;
  }
  await setDoc(
    doc(db, "games", gameCode, "answers", `${current}-${team}`),
    { ...payload, submittedAt: serverTimestamp(), time: payload.time || Date.now() },
    { merge: true }
  );
};

const handleDragEnd = async (result) => {
  if (!result?.destination) return;
  if (timer === 0) return;

  const nextOrder = (() => {
    const a = orderedAnswers.slice();
    const [moved] = a.splice(result.source.index, 1);
    a.splice(result.destination.index, 0, moved);
    return a;
  })();

  setOrderedAnswers(nextOrder);

  if (!team) return;
  if (!Array.isArray(nextOrder) || nextOrder.length === 0) return;

  try {
    await submitAnswerDoc({
      team,
      q: current,
      answer: nextOrder,
    });
  } catch (err) {
    setFeedback("Error submitting answer. Try again.");
  }
};




  // Map from displayIdx (shuffled) to actual answerIdx in q.answers array
// Player.js — REPLACE whole function
const handleVanishingChoiceInstantSubmit = async (canonicalIdx) => {
  if (!team || timer === 0) return;
  if (
    typeof canonicalIdx !== "number" ||
    !vanishingIndexes.includes(canonicalIdx)
  ) {
    return;
  }
  const visibleOptionsAtAnswer = vanishingIndexes.length;
  try {
    await submitAnswerDoc({
      team,
      q: current,
      answer: canonicalIdx,
      visibleOptionsAtAnswer,
    });
    setAnswered(true);
  } catch (err) {
    setFeedback("Error submitting answer. Try again.");
  }
};


  const selfieButtonStyle = {
    background: "linear-gradient(135deg, #99ffc9, #009a63 88%)",
    color: "#222",
    fontWeight: 700,
    fontSize: "1.45rem",
    border: "none",
    outline: "none",
    borderRadius: "2em",
    padding: "0.7em 2.3em",
    margin: "0 0 12px 0",
    boxShadow: "0 3px 16px #0002, 0 2px 6px #fff3 inset",
    cursor: "pointer",
    transition: "filter 0.2s, opacity 0.2s",
    filter: "brightness(0.98)",
    display: "inline-block",
    textAlign: "center",
  };

  const openWebSelfieFilePicker = () => {
    document.getElementById("team-selfie-upload")?.click();
  };

  const handlePickSelfieFromLibrary = async () => {
    if (uploading) return;

    if (shouldUseNativeSelfiePicker()) {
      try {
        const file = await pickTeamSelfieFromLibrary();
        setSelfieFile(file);
      } catch (err) {
        if (isSelfiePickCancelled(err)) return;
        if (isCameraPluginUnavailable(err)) {
          openWebSelfieFilePicker();
          return;
        }
        alert("Could not pick photo: " + err.message);
      }
      return;
    }

    openWebSelfieFilePicker();
  };

  const handleTakeSelfiePhoto = async () => {
    if (uploading) return;

    if (shouldUseNativeSelfiePicker()) {
      try {
        // Simulator has no camera — open photo library directly.
        const file = isLikelyIosSimulator()
          ? await pickTeamSelfieFromLibrary()
          : await pickTeamSelfieFromCamera();
        setSelfieFile(file);
      } catch (err) {
        if (isSelfiePickCancelled(err)) return;
        if (isCameraPluginUnavailable(err)) {
          openWebSelfieFilePicker();
          return;
        }
        alert("Could not take photo: " + err.message);
      }
      return;
    }

    openWebSelfieFilePicker();
  };

  const handleJoin = async () => {
    const teamName = inputRef.current.value.trim();
    if (!teamName) {
      alert("Enter a team name!");
      return;
    }
    if (!selfieFile) {
      alert("Please upload a team selfie! (vertical photo please!)");
      return;
    }
    setUploading(true);
    try {
      const downloadURL = await joinTeamWithSelfie({ gameCode, teamName, selfieFile });
      setSelfieUrl(downloadURL);
      setTeam(teamName);
      // Save to localStorage for reconnection
      localStorage.setItem("player_team", teamName);
      localStorage.setItem("player_gameCode", gameCode);
      localStorage.setItem("player_selfieUrl", downloadURL);
      localStorage.setItem("player_wizardStep", "2"); // Joined = step 2
    } catch (err) {
      alert("Failed to upload selfie: " + err.message);
    } finally {
      setUploading(false);
    }
  };




  // Submit logic for ALL types
// Submit logic for ALL types
const handleSubmit = async () => {
  if (!team) return alert("Enter a team name!");

  let points = 0;
  let correct = false;


  // 👇 ADD THIS BLOCK AT THE TOP OF THE FUNCTION 👇
    if ((q.type || "").toUpperCase() === "OUTOFORDER") {
      // Only submit if we have answers (auto-submits when timer = 0)
      if (Array.isArray(orderedAnswers) && orderedAnswers.length > 0) {
        setAnswered(true);
        await submitAnswerDoc({
          team,
          q: current,
          answer: orderedAnswers,
        });
      }
      return; // End submission for this type
    }


  // NEARESTANSWER — store canonical server time for tie-breaks
  if ((q.type || "").toUpperCase() === "NEARESTANSWER" || (q.type || "").toUpperCase() === "JACKPOT") {
    await submitAnswerDoc({
      team,
      q: current,
      answer: Number(numberAnswer),
    });
    setAnswered(true);
    return;
  }

  // BREADCRUMBS — multiple attempts, append to attempts array; lock when correct
  if ((q.type || "").toUpperCase() === "BREADCRUMBS") {
    function normalizeAnswer(s) {
      return (s || "").replace(/[^A-Z0-9]/gi, "").toUpperCase();
    }
    function getSimilarity(a, b) {
      const dp = Array(a.length + 1)
        .fill(null)
        .map(() => Array(b.length + 1).fill(null));
      for (let i = 0; i <= a.length; i++) dp[i][0] = i;
      for (let j = 0; j <= b.length; j++) dp[0][j] = j;
      for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
          const cost = a[i - 1] === b[j - 1] ? 0 : 1;
          dp[i][j] = Math.min(
            dp[i - 1][j] + 1,
            dp[i][j - 1] + 1,
            dp[i - 1][j - 1] + cost
          );
        }
      }
      const distance = dp[a.length][b.length];
      const maxLen = Math.max(a.length, b.length);
      return maxLen === 0 ? 1 : 1 - distance / maxLen;
    }
    const playerAnswer = normalizeAnswer(numberAnswer);
    const correctList = (q.answers || []).filter(Boolean).map((a) => normalizeAnswer(a));
    const isCorrect = correctList.length > 0 && correctList.some((c) => getSimilarity(playerAnswer, c) >= 0.8);
    const answerRef = doc(db, "games", gameCode, "answers", `${current}-${team}`);
    const attemptEntry = { answer: numberAnswer, submittedAt: Timestamp.now() };
    const isNativeApp = typeof window !== "undefined" && window.Capacitor;
    const existingAnswer = isNativeApp
      ? await fetchFirestoreDocViaRest(
          `games/${encodeURIComponent(gameCode)}/answers/${encodeURIComponent(`${current}-${team}`)}`
        )
      : null;
    const snap = isNativeApp ? null : await getDoc(answerRef);
    if (isNativeApp ? !existingAnswer : !snap.exists()) {
      if (typeof window !== "undefined" && window.Capacitor) {
        await submitAnswerDoc({
          team,
          q: current,
          attempts: [attemptEntry],
          correct: isCorrect,
        });
      } else {
        await setDoc(answerRef, {
        team,
        q: current,
        attempts: [attemptEntry],
        correct: isCorrect,
      }, { merge: true });
      }
    } else {
      if (typeof window !== "undefined" && window.Capacitor) {
        const existing = existingAnswer || {};
        await submitAnswerDoc({
          team,
          q: current,
          attempts: [...(existing.attempts || []), attemptEntry],
          ...(isCorrect ? { correct: true } : {}),
        });
      } else {
        await updateDoc(answerRef, {
          attempts: arrayUnion(attemptEntry),
          ...(isCorrect ? { correct: true } : {}),
        });
      }
    }
    if (isCorrect) setAnswered(true);
    setFeedback(isCorrect ? "Correct!" : "Wrong – try again!");
    return;
  }

  // WHEEL and TYPE — unchanged logic, now also server-stamps
  if ((q.type || "").toUpperCase() === "WHEEL" || (q.type || "").toUpperCase() === "TYPE") {
    function normalizeAnswer(s) {
      return (s || "").replace(/[^A-Z0-9]/gi, "").toUpperCase();
    }
    function getSimilarity(a, b) {
      const dp = Array(a.length + 1)
        .fill(null)
        .map(() => Array(b.length + 1).fill(null));
      for (let i = 0; i <= a.length; i++) dp[i][0] = i;
      for (let j = 0; j <= b.length; j++) dp[0][j] = j;
      for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
          const cost = a[i - 1] === b[j - 1] ? 0 : 1;
          dp[i][j] = Math.min(
            dp[i - 1][j] + 1,
            dp[i][j - 1] + 1,
            dp[i - 1][j - 1] + cost
          );
        }
      }
      const distance = dp[a.length][b.length];
      const maxLen = Math.max(a.length, b.length);
      return maxLen === 0 ? 1 : 1 - distance / maxLen;
    }

    const playerAnswer = normalizeAnswer(numberAnswer);
    const correctAnswer = normalizeAnswer(q.answers[0]);
    const similarity = getSimilarity(playerAnswer, correctAnswer);
    // An empty answer never counts (an empty guess against an empty expected answer would score 1.0).
    correct = correctAnswer.length > 0 && playerAnswer.length > 0 && similarity >= 0.8;

    setAnswered(true);
    await submitAnswerDoc({
      team,
      q: current,
      answer: numberAnswer,
      correct,
      similarity: similarity.toFixed(2),
    });
    return;
  }

  const qTypeUpper = String(q?.type || q?.CAT || q?.cat || q?.TYPE || q?.questionType || "").trim().toUpperCase();
  const isVanishingChoice = qTypeUpper === "VANISHINGCHOICE";

  // VANISHINGCHOICE — save visible count + canonical time; host scores later
  if (isVanishingChoice) {
    if (selected.length !== 1) {
      setFeedback("Select an answer!");
      return;
    }
    const answerIdx = (q.answersOrder || q.answers.map((_, i) => i))[selected[0]];
    const visibleOptionsAtAnswer = vanishingIndexes.length;

    await submitAnswerDoc({
      team,
      q: current,
      answer: answerIdx,
      visibleOptionsAtAnswer,
    });
    setAnswered(true);
    return;
  }

  // MULTICHOICE / TRUE/FALSE / BETANSWER / MULTIANSWER — unchanged scoring client-side
  if (
    (q.type || "").toUpperCase() === "BETANSWER" ||
    (q.type || "").toUpperCase() === "MULTICHOICE" ||
    (q.type || "").toUpperCase() === "TRUE/FALSE"
  ) {
    const correctIndexes = [getDisplayAnswerOrder(q).findIndex((i) => i === 0)];
    correct = selected.length === 1 && selected[0] === correctIndexes[0];
    points = correct ? (q.points || 0) : 0;
  } else if ((q.type || "").toUpperCase() === "MULTIANSWER") {
    const correctIndexes =
      (q.answersOrder
        ? q.answersOrder.map((origIdx, displayIdx) =>
            /^\[.*\]$/.test(game.questions[current].answers[origIdx])
              ? displayIdx
              : null
          )
        : q.answers.map((ans, i) => (/^\[.*\]$/.test(ans) ? i : null))
      ).filter((i) => i !== null);
    const numCorrect = selected.filter((idx) => correctIndexes.includes(idx)).length;
    const perCorrect =
      typeof q.points === "number" && !isNaN(q.points) ? q.points : Number(q.points) || 5;
    points = numCorrect * perCorrect;
    correct = numCorrect > 0;
  }

  setFeedback(correct ? "Correct!" : "Incorrect.");
  setAnswered(true);
  await submitAnswerDoc({
    team,
    q: current,
    answer: (q.type || "").toUpperCase() === "MULTIANSWER"
      ? selected.map(displayIdx => (q.answersOrder || q.answers.map((_, i) => i))[displayIdx])
      : selected.length
        ? getDisplayAnswerOrder(q)[selected[0]]
        : -1,
    correct,
    points,
  });
};

// Store handleSubmit in ref for auto-submit effect
handleSubmitRef.current = handleSubmit;

  // At the end of your Player component:
 // At the end of your Player component:
  if (wizardStep === 0) {
    // Step 1: Team name input
    return (
      <div
        style={{
          ...brainScreenStyle(),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
          <form
          onSubmit={async (e) => { // Make it async
              e.preventDefault();
              setNameValidationFeedback("Checking..."); // Show feedback

              const rawName = inputRef.current.value;
              const teamName = rawName.trim(); // Use original case name

              // 1. Basic Empty Check
              if (!teamName) {
                setNameValidationFeedback("Please enter a team name.");
                return;
              }

              // 2. Length Check
              if (teamName.length > MAX_TEAM_NAME_LENGTH) {
                setNameValidationFeedback(`Name too long (max ${MAX_TEAM_NAME_LENGTH} characters).`);
                return;
              }

              // 3. Profanity Filter Check
              if (isProfane(teamName)) {
                setNameValidationFeedback("I'm sorry but your team name has offended our censors (it probably contains a rude word).");
                return;
              }

              // 4. Uniqueness Check (Fetch all teams and compare lowercase)
              try {
                if (!gameCode) {
                    setNameValidationFeedback("Cannot verify name: Missing game code.");
                    console.error("gameCode is missing in onSubmit");
                    return;
                }

                // --- Fetch ALL teams for this game ---
                const teamRows = (typeof window !== "undefined" && window.Capacitor)
                  ? await fetchTeamsViaRest(gameCode)
                  : await getDocs(collection(db, "games", gameCode, "teams")).then((snap) =>
                      snap.docs.map((doc) => ({ id: doc.id, data: doc.data() }))
                    );

                const proposedNameLower = teamName.toLowerCase(); // Lowercase version of the name being entered
                let isDuplicate = false;

                // --- Iterate and compare lowercase names ---
// --- Iterate and compare names for high similarity ---
         let isTooSimilar = false;
                let similarToName = "";
                const similarityThreshold = 0.90; // 90%

                teamRows.forEach((doc) => {
                  if (isTooSimilar) return; // Stop checking if we already found a conflict

                  const existingTeamData = typeof doc.data === "function" ? doc.data() : doc.data;
                  // Prefer 'team' field, fall back to doc.id
                  const existingName = existingTeamData?.team || doc.id;

                  // Guard against comparing to empty/invalid names
                  if (existingName && typeof existingName === 'string' && existingName.trim()) {
                    // Calculate similarity (case/space insensitive)
                    const similarity = calculateNormalizedSimilarity(teamName, existingName);

                    if (similarity >= similarityThreshold) {
                      isTooSimilar = true;
                      similarToName = existingName; // Store the name it clashed with
                    }
                  }
                });
                // --- End of iteration ---

                if (isTooSimilar) {
                  // Name is too similar (case/space insensitive)
                  // Use the originally typed name 'teamName' in the message
                  setNameValidationFeedback(`Team name "${teamName}" is too similar to another team! Please choose another.`);
                  return; // Stop execution
                }
                // All checks passed!
                setNameValidationFeedback(""); // Clear feedback
                setTeam(teamName); // Set state using the original case name
                setWizardStep(1); // Proceed to the next step
                // Save team name to localStorage (gameCode comes from URL)
                localStorage.setItem("player_team", teamName);
                if (gameCode) localStorage.setItem("player_gameCode", gameCode);

              } catch (error) {
                console.error("Error checking team name uniqueness:", error);
                setNameValidationFeedback("Error checking name. Please try again.");
              }
            }}





            style={{
              width: "100%",
              maxWidth: 430,
              padding: "2em 2em 2.5em 2em",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <div style={wizardTitleStyle}>
              Enter your team name
            </div>
            <input
              placeholder="Team Name"
              ref={inputRef}
              maxLength={MAX_TEAM_NAME_LENGTH}
              spellCheck={false}
              autoCorrect="off"
              autoComplete="off"
              disabled={uploading || nameValidationFeedback.startsWith("Checking")} // Disable while checking
              style={{
                fontSize: "1.2em",
                borderRadius: "1em",
                padding: "0.5em 1em",
                border: "2px solid rgba(255,255,255,0.45)",
                width: "95%",
                maxWidth: 340,
                textAlign: "center",
                fontWeight: 700,
                background: "rgba(255,255,255,0.92)",
              }}
              autoFocus
            />
            {nameValidationFeedback && (
              <div style={{
                marginTop: '8px',
                marginBottom: '8px',
                color: nameValidationFeedback.startsWith("Checking") ? 'rgba(255,255,255,0.85)' : '#ffb3b3',
                fontSize: '0.9em',
                fontWeight: 'bold',
                minHeight: '1.2em',
                textAlign: 'center',
                width: '95%',
                maxWidth: 340,
                textShadow: '0 1px 4px #000',
              }}>
                {nameValidationFeedback}
              </div>
            )}
            <button
              type="submit"
              style={{
                background: "linear-gradient(135deg, #99ffc9, #009a63 88%)",
                color: "#222",
                fontWeight: 700,
                fontSize: "1.45rem",
                border: "none",
                outline: "none",
                borderRadius: "2em",
                padding: "0.7em 2.3em",
                margin: "16px 0",
                boxShadow: "0 3px 16px #0002, 0 2px 6px #fff3 inset",
                cursor: nameValidationFeedback.startsWith("Checking") ? "wait" : "pointer",
                transition: "filter 0.2s, opacity 0.2s",
                filter: "brightness(0.98)",
                opacity: nameValidationFeedback.startsWith("Checking") ? 0.7 : 1,
              }}
              disabled={uploading || nameValidationFeedback.startsWith("Checking")} // Disable while checking
            >
              Next
            </button>
          </form>
      </div>
    );
  }

  // Show restoring indicator
  if (restoring) {
    return (
      <div style={{
        ...brainScreenStyle(),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        color: "#fff",
      }}>
        <div style={{ fontSize: "1.5rem", marginBottom: "1rem" }}>Reconnecting...</div>
        <div style={{ fontSize: "1rem", opacity: 0.7 }}>Restoring your game progress...</div>
      </div>
    );
  }

if (wizardStep === 1) {
  // Step 2: Selfie upload
  return (
    <div
      style={{
        ...brainScreenStyle(),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        position: "relative",
      }}
    >
        {uploading && (
          <>
            <style>{`
              @keyframes selfieUploadSpin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              @keyframes selfieUploadPulse {
                0%, 100% { opacity: 0.55; transform: scale(0.98); }
                50% { opacity: 1; transform: scale(1); }
              }
            `}</style>
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 5000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                background: "rgba(12, 8, 28, 0.72)",
                backdropFilter: "blur(4px)",
                WebkitBackdropFilter: "blur(4px)",
                padding: "1.5em",
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  border: "5px solid rgba(255,255,255,0.22)",
                  borderTopColor: "#99ffc9",
                  animation: "selfieUploadSpin 0.85s linear infinite",
                  marginBottom: 18,
                  boxShadow: "0 0 24px rgba(153,255,201,0.35)",
                }}
              />
              <div
                style={{
                  fontSize: "1.35em",
                  fontWeight: 900,
                  color: "#fff",
                  textShadow: "0 0 8px #000, 0 2px 12px #000",
                  marginBottom: 8,
                  animation: "selfieUploadPulse 1.4s ease-in-out infinite",
                }}
              >
                Uploading your team selfie…
              </div>
              <div
                style={{
                  fontSize: "0.95em",
                  color: "rgba(255,255,255,0.85)",
                  fontWeight: 600,
                  textShadow: "0 1px 4px #000",
                }}
              >
                {selfieFile ? formatSelfieFileSize(selfieFile.size) : ""} — please wait
              </div>
            </div>
          </>
        )}
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!selfieFile) {
              alert("Please upload a team selfie (vertical photo please!)");
              return;
            }
            setUploading(true);
            try {
              const teamName = team;
              if (!teamName) {
                alert("Missing team name! Go back and enter it.");
                return;
              }
              const downloadURL = await joinTeamWithSelfie({ gameCode, teamName, selfieFile });
              setSelfieUrl(downloadURL);
              setTeam(teamName);
              setWizardStep(2);
              // Save to localStorage for reconnection
              localStorage.setItem("player_team", teamName);
              localStorage.setItem("player_gameCode", gameCode);
              localStorage.setItem("player_selfieUrl", downloadURL);
              localStorage.setItem("player_wizardStep", "2");
            } catch (err) {
              alert("Failed to upload selfie: " + err.message);
            } finally {
              setUploading(false);
            }
          }}
          style={{
            width: "100%",
            maxWidth: 430,
            padding: "2em 2em 2.5em 2em",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <div style={wizardTitleStyle}>
            Please take a team selfie in portrait mode (vertical)
          </div>
          <input
            id="team-selfie-upload"
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const picked = e.target.files?.[0];
              if (!picked) return;
              try {
                setSelfieFile(await compressSelfieFile(picked));
              } catch (err) {
                alert("Could not prepare photo: " + err.message);
              }
            }}
            disabled={uploading}
            style={{
              display: "none"
            }}
          />
          <button
            type="button"
            onClick={handleTakeSelfiePhoto}
            disabled={uploading}
            style={{
              ...selfieButtonStyle,
              cursor: uploading ? "wait" : "pointer",
              opacity: uploading ? 0.7 : 1,
            }}
          >
            Take Photo
          </button>
          <div
            style={{
              fontSize: "1.05em",
              fontWeight: 700,
              color: "rgba(255,255,255,0.9)",
              margin: "0 0 12px 0",
              textShadow: "0 1px 4px #000",
            }}
          >
            or
          </div>
          <button
            type="button"
            onClick={handlePickSelfieFromLibrary}
            disabled={uploading}
            style={{
              ...selfieButtonStyle,
              cursor: uploading ? "wait" : "pointer",
              opacity: uploading ? 0.7 : 1,
            }}
          >
            Choose Photo
          </button>
          <div
            style={{
              fontSize: "0.9em",
              marginBottom: 12,
              color: "rgba(255,255,255,0.85)",
              fontWeight: "bold",
              textShadow: "0 1px 4px #000",
            }}
          >
            {selfieFile
              ? `Photo selected · ${formatSelfieFileSize(selfieFile.size)}`
              : "No photo chosen"}
          </div>
          {selfieFile && (
            <img
              src={URL.createObjectURL(selfieFile)}
              alt="Team Selfie Preview"
              style={{
                width: 140,
                borderRadius: 18,
                marginBottom: 18,
                boxShadow: "0 2px 12px rgba(0,0,0,0.5)",
                border: "2px solid rgba(255,255,255,0.45)",
              }}
            />
          )}
          <button
            type="submit"
            style={{
              background: "linear-gradient(135deg, #99ffc9, #009a63 88%)",
              color: "#222",
              fontWeight: 700,
              fontSize: "1.45rem",
              border: "none",
              outline: "none",
              borderRadius: "2em",
              padding: "0.7em 2.3em",
              margin: "16px 0",
              boxShadow: selfieFile ? "0 3px 16px #0002, 0 2px 6px #fff3 inset" : "none",
              cursor: !selfieFile ? "not-allowed" : uploading ? "wait" : "pointer",
              transition: "filter 0.2s, opacity 0.2s",
              filter: selfieFile ? "brightness(0.98)" : "grayscale(0.35) brightness(0.85)",
              opacity: !selfieFile ? 0.42 : uploading ? 0.7 : 1,
            }}
            disabled={!selfieFile || uploading}
          >
            {uploading ? "Uploading..." : "Join"}
          </button>
        </form>
    </div>
  );
}


  // Show the main game UI only if wizardStep === 2 and team is set
  if (wizardStep === 2 && team) {
    // ... Everything from your {team && ( ... )} block goes here! ...
    // (The entire game UI. Just paste it in here.)
 return (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100dvh",
      display: "flex",
      flexDirection: "column",
      background: "transparent",   // CHANGED: let the background image show through
      boxSizing: "border-box",
      overflow: "hidden",
      padding: 0,
      margin: 0,
      zIndex: 1,                   // sits above the background layer
      paddingBottom: shouldShowTimer ? PLAYER_TIMER_BAR_RESERVE : 0,
    }}
  >
    {/* Background image layer (behind everything) */}
    <PlayerBackground q={qMemo} />

    {/* The question is held back until the big screen shows it (hosts write questionShownQ). */}
    {(() => {
      const t = String(qMemo?.type || qMemo?.CAT || "").trim().toUpperCase();
      const slideTypes = ["DISPLAY", "SLIDE", "DEFAULT", "DEFAULT CATEGORY", "STARTROUND", "ENDROUND", "SHOWROUNDS",
        "INTRO", "INTRODUCTION", "JOIN QUIZ", "END QUIZ", "BREAK", "BREAK TIME", ""];
      const hidden = typeof game?.questionShownQ === "number" && game.questionShownQ !== current && !slideTypes.includes(t) && !answered;
      return hidden ? <GetReadyCard q={qMemo} number={displayQuestionNumber} /> : null;
    })()}
    {String(qMemo?.type || qMemo?.CAT || "").trim().toUpperCase() === "END QUIZ" && (
      <EndOfQuizResults gameCode={gameCode} game={game} team={team} />
    )}

    {(teamChoiceSession?.phase === "awaitingPick" || teamChoiceSession?.phase === "revealPause") &&
      game &&
      getTeamRoundChoiceSettingsFromGameData(game).enabled && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 40,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            padding:
              "max(6px, env(safe-area-inset-top)) 12px max(16px, env(safe-area-inset-bottom)) 12px",
            boxSizing: "border-box",
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.65) 40%, rgba(0,0,0,0.9) 100%)",
            color: "#fff",
            textAlign: "center",
            pointerEvents: "auto",
            overflow: "hidden",
          }}
        >
          {(() => {
            const isReveal = teamChoiceSession.phase === "revealPause";
            const secsLeft =
              teamChoiceSession.phase === "awaitingPick" && teamChoiceSession.deadlineAtMs
                ? Math.max(0, Math.ceil((teamChoiceSession.deadlineAtMs - Date.now()) / 1000))
                : isReveal && teamChoiceSession.revealUntilMs
                  ? Math.max(0, Math.ceil((teamChoiceSession.revealUntilMs - Date.now()) / 1000))
                  : 0;
            void teamChoiceTick;
            const chooser = teamChoiceSession.eligibleTeam || "—";
            return (
              <>
                <div style={{ maxWidth: "96vw", marginBottom: 6 }}>
                  <div
                    style={{
                      fontSize: "clamp(0.95rem, 3vw, 1.2rem)",
                      fontWeight: 700,
                      marginBottom: 4,
                      lineHeight: 1.2,
                    }}
                  >
                    {teamChoiceSession.eligibleTeam ? "Please select the next round:" : "Picking the next round at random…"}
                  </div>
                  <div
                    style={{
                      display: teamChoiceSession.eligibleTeam ? undefined : "none",
                      fontSize: "clamp(1.45rem, 5vw, 2.5rem)",
                      fontWeight: 900,
                      lineHeight: 1.15,
                      textShadow: "0 2px 12px rgba(0,0,0,0.85)",
                      wordBreak: "break-word",
                    }}
                  >
                    {chooser}
                  </div>
                </div>
                {isReveal && teamChoiceSession.chosenTag ? (
                  <div
                    style={{
                      fontSize: "clamp(1rem, 3vw, 1.25rem)",
                      marginBottom: 8,
                      fontWeight: 800,
                    }}
                  >
                    Selected for this round:{" "}
                    <strong style={{ color: "#86efac" }}>#{teamChoiceSession.chosenTag}</strong>
                  </div>
                ) : null}
                <div
                  style={{
                    fontSize: "min(11vw, 3.5rem)",
                    fontWeight: 900,
                    marginBottom: 8,
                    textShadow: "0 2px 10px rgba(0,0,0,0.75)",
                    lineHeight: 1,
                  }}
                >
                  {secsLeft}
                </div>
              </>
            );
          })()}
          <ol
            type="A"
            className="player-answer-list answers-stagger-in"
            style={{
              flex: "1 1 0",
              minHeight: 0,
              width: "100%",
              maxWidth: "92vw",
              overflowY: "auto",
              padding: 0,
              margin: 0,
              listStyle: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: "2.5vh",
            }}
          >
            {(teamChoiceSession.tagOptions || []).map((tg, displayIdx) => {
              const letter = String.fromCharCode(97 + (displayIdx % 6));
              const isReveal = teamChoiceSession.phase === "revealPause";
              const chosen = teamChoiceSession.chosenTag;
              const isChosen = isReveal && chosen === tg;
              const canPick =
                !isReveal && team === teamChoiceSession.eligibleTeam;
              const dimOthers = isReveal && !isChosen;
              return (
                <li
                  key={tg}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    flex: "1 1 0",
                    minHeight: 0,
                    margin: 0,
                    padding: 0,
                    listStyle: "none",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => canPick && submitTeamRoundTag(tg)}
                    disabled={!canPick || isReveal}
                    className={
                      "player-answer-option player-option-" +
                      letter +
                      (isChosen ? " correct" : "") +
                      (dimOthers || (!canPick && !isReveal) ? " ghosted" : "")
                    }
                    style={{
                      width: "min(900px, 86vw)",
                      margin: "6px auto",
                      cursor: canPick ? "pointer" : "not-allowed",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PlayerAnswerFitText text={`#${tg}`} style={{ width: "100%" }} />
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      )}

    {/* TIMER ROW */}







        {/* QUESTION PROMPT */}

        
<div
  className={`question-prompt${isBlock ? " block" : ""}`}
  style={getPlayerQuestionPillStyle(isBlock, {
    isFirst: true,
    compactBottom: showLobbyWaiting,
    plainText: questionDisplayText,
  })}
>
{
  questionDisplayText.split("{LB}").map((part, idx, arr) =>
    idx < arr.length - 1
      ? <React.Fragment key={idx}>{part}<br /></React.Fragment>
      : part
  )
}
{getMultiAnswerSuffix(q)}
</div>

{showLobbyWaiting && (
  <>
    <div style={getPlayerQuestionPillStyle(isBlock)}>
      {WAITING_FOR_QUIZMASTER_TEXT}
    </div>
    {shouldShowPhoneSetupTips() && <PhoneSetupTipsCard />}
  </>
)}




{q.type === "BETANSWER" && timer > 30 && (

<form
    onSubmit={async (e) => {
      e.preventDefault();

      if (!team) return;

      setBetSubmitted(true);

      try {
        // Native app: the Firestore SDK never resolves in the WebView, so write via REST (field-only update).
        if (typeof window !== "undefined" && window.Capacitor) {
          await updateFirestoreFieldsViaRest(
            `games/${encodeURIComponent(gameCode)}/teams/${encodeURIComponent(team)}`,
            { currentbet: betAmount }
          );
        } else {
          await setDoc(
            doc(db, "games", gameCode, "teams", team),
            {
              currentbet: betAmount,
            },
            { merge: true }
          );
        }

        console.log("✅ Bet stored for team:", team, "bet:", betAmount);
      } catch (err) {
        console.error("❌ Failed to store currentbet in team doc:", err);
      }
    }}


    style={{
      textAlign: "center",
      margin: "4vh auto 0 auto",
      padding: "2.5em 2em",
      background: "transparent",
      borderRadius: "16px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 18,
      maxWidth: 420,
    }}
  >
    <label style={{
      fontWeight: 900,
      fontSize: "clamp(1.43rem, 3.51vw, 2.86rem)",
      fontFamily: "'QuizPlayerMontserrat', 'Montserrat', 'Arial Black', Arial, sans-serif",
      textShadow: "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
      color: "#fff",
      marginBottom: 8,
      lineHeight: 1.19,
    }}>
      How many points would you like to bet?
    </label>
<select
  value={betAmount}
  onChange={e => setBetAmount(Number(e.target.value))}
  disabled={betSubmitted}
  style={{
    fontSize: "2.1rem",
    textAlign: "center",
    border: "2px solid #4e63ff",
    borderRadius: "0.6em",
    width: "130px",
    padding: "0.6em",
    boxShadow: "0 2px 10px #0002",
    background: betSubmitted ? "#eee" : "#f9f9fa",
    fontWeight: 700,
    opacity: betSubmitted ? 0.6 : 1,
    cursor: betSubmitted ? "not-allowed" : "pointer"
  }}
>
  <option value={0}>0</option>
  <option value={5}>5</option>
  <option value={10}>10</option>
  <option value={15}>15</option>
  <option value={20}>20</option>
  <option value={25}>25</option>
</select>

<button
  type="submit"
  disabled={betSubmitted}
  style={{
    marginTop: "1.5em",
    fontSize: "1.35rem",
    fontWeight: "bold",
    background: "linear-gradient(90deg, #50c5fc 60%, #6b93f7 100%)",
    color: "#132155",
    border: "none",
    borderRadius: "0.6em",
    padding: "0.75em 2.1em",
    boxShadow: "0 2px 8px #49b6e366",
    opacity: betSubmitted ? 0.6 : 1,
    cursor: betSubmitted ? "not-allowed" : "pointer",
    transition: "background 0.18s"
  }}
>
  {betSubmitted ? "SUBMITTED" : "Submit Bet"}
</button>


    <div style={{ fontSize: "1.08rem", color: "#89d", marginTop: 4 }}>
      (0 to 25 points)
    </div>
  </form>
)}





        {/* ANSWERS BLOCK */}


 <form 
  onSubmit={(e) => { 
    e.preventDefault(); 
    e.stopPropagation();
    // OUTOFORDER now auto-submits on timer = 0, but still allow manual submit if needed
    if ((q.type || "").toUpperCase() === "OUTOFORDER" && (!Array.isArray(orderedAnswers) || orderedAnswers.length === 0)) {
      return; // Don't submit if empty
    }
    handleSubmit(); 
  }} 
  style={{ flex: 1, width: "100%", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}
>
          {(q.type || "").toUpperCase() === "OUTOFORDER" ? (
  orderedAnswers.length > 0 ? (
  <React.Fragment>
  <DragDropContext onDragEnd={handleDragEnd}>
    <Droppable droppableId="ooo">
      {(provided) => (
        <ol
          className="player-answer-list"
          ref={provided.innerRef}
          {...provided.droppableProps}
          style={{ listStyle: "none", padding: 0 }}
        >
{orderedAnswers.map((answerIdx, displayIdx) => {
  // stable colour class from the answer's original slot
  const slot = (oooColorSlot?.[answerIdx] ?? (answerIdx % 6)) % 6;
  const letterClass = `player-option-${String.fromCharCode(97 + slot)}`;

  // Get answer value from answers array or A1-A6 fields
  const answersArray = Array.isArray(q.answers) ? q.answers : [];
  const fallbackAnswers = [q?.A1, q?.A2, q?.A3, q?.A4, q?.A5, q?.A6];
  const value = answersArray[answerIdx] ?? fallbackAnswers[answerIdx] ?? "";

  // We did NOT reorder on reveal, so the displayed list is the player's order.
  // An item is correct if it's sitting at its canonical index.
  const isRevealed = !timerActive;
  const isCorrectPosition = isRevealed && orderedAnswers[displayIdx] === displayIdx;
  const isIncorrectPosition = isRevealed && !isCorrectPosition;

  return (
    <Draggable
      key={`ooo-${answerIdx}`}
      draggableId={`ooo-${answerIdx}`}
      index={displayIdx}
      isDragDisabled={answered || timer === 0}
    >
      {(dragProvided, snapshot) => (
        <li
          ref={dragProvided.innerRef}
          {...dragProvided.draggableProps}
          {...dragProvided.dragHandleProps}
          style={{
            ...dragProvided.draggableProps.style,
            display: "flex",
            justifyContent: "center",
            alignItems: "stretch",
            flex: "1 1 0",
            minHeight: 0,
            width: "100%",
            margin: 0,
            padding: 0,
            listStyle: "none",
          }}
        >
        <div
          className={`player-answer-option ${letterClass}${
            isCorrectPosition ? " correct-glimmer" : ""
          }${isIncorrectPosition ? " incorrect-halo" : ""}`}
          style={{
            width: "min(760px, 80vw)",
            position: "relative",
            paddingLeft: "2.4em",
            boxSizing: "border-box",
            fontWeight: 900,
            textAlign: "center",
            cursor: answered || timer === 0 ? "default" : "grab",
            boxShadow: snapshot.isDragging
              ? "0 0 0 3px rgba(80,150,255,.55), 0 6px 24px rgba(0,0,0,.35)"
              : undefined,
            userSelect: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ position: "absolute", left: "0.45em", top: "50%", transform: "translateY(-50%)", width: "1.6em", height: "1.6em",
            borderRadius: "50%", background: "linear-gradient(180deg, #fff7c2, #f2c94c)", color: "#4b3b00", fontSize: "0.85em",
            display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(0,0,0,0.4)" }}>{displayIdx + 1}</span>
          <PlayerAnswerFitText text={value} style={{ width: "100%", height: "100%" }} />
        </div>

        </li>
      )}
    </Draggable>
  );
})}



          {provided.placeholder}
        </ol>
      )}
    </Droppable>
  </DragDropContext>
  </React.Fragment>
  ) : (
    <div style={{ textAlign: "center", padding: "2em", color: "#fff" }}>
      Loading options...
    </div>
  )
) :




(q.type === "WHEEL" || q.type === "TYPE" || q.type === "BREADCRUMBS") ? (

  <div style={{
    width: "100%",
    margin: "2em 0 0 0",
    padding: 0,
    display: "block"
  }}>
<textarea
  placeholder="TYPE THE ANSWER"
  value={numberAnswer}
  onChange={(e) => setNumberAnswer(e.target.value.toUpperCase())}
  disabled={answered || timer === 0}
  rows={3}
  style={{
    width: "90%",
    maxWidth: "400px",
    minWidth: "60px",
    fontSize: "1.2em",
    textAlign: "left",
    borderRadius: "0.75em",
    border: "2px solid #222",
    textTransform: "uppercase",
    letterSpacing: "0.10em",
    background: answered ? "#eee" : "#fff",
    opacity: answered ? 0.6 : 1,
    boxShadow: "0 1px 7px #0003",
    outline: "none",
    padding: "0.65em 0.45em",
    fontFamily: "'Montserrat', Arial, Helvetica, sans-serif",
    color: "#222",
    margin: "0 auto 26px auto",
    display: "block",
    resize: "vertical",
    minHeight: "60px",
    lineHeight: "1.4",
    wordWrap: "break-word",
    overflowWrap: "break-word",
  }}
  autoFocus
  className="wheel-input"
/>

<button
  type="submit"
  disabled={answered || timer === 0 || !numberAnswer.trim()}
  className="player-answer-option player-option-a"
  style={{
    width: "min(900px, 86vw)",
    height: PLAYER_ANSWER_ROW_HEIGHT,
    margin: "24px auto 6px auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: answered || timer === 0 || !numberAnswer.trim() ? "not-allowed" : "pointer",
    opacity: 1,
  }}
>
  <PlayerAnswerFitText
    text={answered ? "Submitted" : "Submit"}
    style={{ width: "100%", height: "100%" }}
  />
</button>

    {feedback && (
      <div style={{ 
        marginTop: 18, 
        fontSize: "1.3em", 
        textAlign: "center",
        fontWeight: 700,
        color: "#fff",
        textShadow: "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
        padding: "0.5em",
        background: "rgba(0, 0, 0, 0.5)",
        borderRadius: "0.5em",
        maxWidth: "90%",
        margin: "12px auto 0 auto",
      }}>
        {feedback}
      </div>
    )}
  </div>

) : ((q.type || "").toUpperCase() === "NEARESTANSWER" || (q.type || "").toUpperCase() === "JACKPOT") ? (
  <div style={{ textAlign: "center", margin: "2em 0" }}>
    <input
      type="number"
      placeholder=""
      value={numberAnswer}
      onChange={(e) => setNumberAnswer(e.target.value)}
      disabled={answered || timer === 0}
      style={{
        fontSize: "2em",
        width: 280,
        textAlign: "center",
        borderRadius: "1em",
        border: "2px solid #999",
        backgroundColor: (answered || timer === 0) ? "rgba(255, 255, 255, 0.95)" : "#fff",
        color: "#222",
        opacity: (answered || timer === 0) ? 1 : 1,
      }}
    />
    <button
      type="submit"
      disabled={answered || timer === 0 || !numberAnswer.trim()}
      style={{
        fontSize: "1.08em",
        borderRadius: "3em",
        padding: "0.7em 2.5em",
        fontWeight: 900,
        fontFamily: "'Montserrat', Arial, Helvetica, sans-serif",
        border: "2px solid rgba(72,221,124,.6)",
        background: "linear-gradient(180deg, #1b5e32, #114822)",
        color: "#eaf3ff",
        textShadow: "0 2px 10px #001a",
        boxShadow:
          "inset 0 2px 4px rgba(255,255,255,0.6)," +
          "inset 0 -2px 6px rgba(0,0,0,0.3)," +
          "0 4px 12px rgba(0,0,0,0.5)",
        cursor:
          answered || timer === 0 || !numberAnswer.trim()
            ? "not-allowed"
            : "pointer",
        opacity:
          answered || timer === 0 || !numberAnswer.trim()
            ? 0.7
            : 1,
        margin: "24px auto 6px auto",
        display: "block",
        transition: "all 0.22s cubic-bezier(.42,.0,.58,1)",
      }}
    >
      {answered ? "Submitted Successfully" : "Submit"}
    </button>
    <div style={{ marginTop: 14, fontSize: "1.2em" }}>{feedback}</div>
  </div>
) : (

(() => {
  const qTypeNorm = normalizeType(q);
  const answerIdxList = getDisplayAnswerOrder(q);

  const useCompactAnswerRows =
    answerIdxList.length > 0 && answerIdxList.length < PLAYER_MC_ANSWER_SLOTS;
  const useCappedAnswerRows = answerIdxList.length >= PLAYER_MC_ANSWER_SLOTS;
  const answerRowLayout = useCappedAnswerRows
    ? getPlayerAnswerRowLayout(answerIdxList.length)
    : null;

  const ghostNormType = String(
    q?.type || q?.CAT || q?.cat || q?.TYPE || q?.questionType || ""
  )
    .trim()
    .toUpperCase();
  const vanishingGhostLocked =
    ghostNormType === "VANISHINGCHOICE" &&
    selected.length >= 1 &&
    !vanishingIndexes.includes(answerIdxList[selected[0]]);

  return (
    <AnswersGate open={answersOpen}>
      <ol
        type="A"
        className={`player-answer-list${
          useCompactAnswerRows ? " player-answer-list--compact" : ""
        }${useCappedAnswerRows ? " player-answer-list--capped" : ""}`}
        style={{
          flex: 1,
          minHeight: 0,
          height: "auto",
          maxHeight: "100%",
          padding: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: useCappedAnswerRows ? "flex-start" : "stretch",
          boxSizing: "border-box",
          overflow: useCappedAnswerRows ? "auto" : "hidden",
          ...(useCappedAnswerRows && answerRowLayout
            ? {
                gap: answerRowLayout.gap,
                "--player-answer-row-h": answerRowLayout.rowHeight,
              }
            : {}),
        }}
      >
        {answerIdxList.map((answerIdx, displayIdx) => {
          // Hide during BETANSWER bet phase
          if (q.type === "BETANSWER" && timer > 30) return null;

          const qTypeUpper = String(q?.type || q?.CAT || q?.cat || q?.TYPE || q?.questionType || "").trim().toUpperCase();
          const isVanishing = qTypeUpper === "VANISHINGCHOICE";
          const isVanished  = isVanishing && !vanishingIndexes.includes(answerIdx);
          const isMulti     = q.type === "MULTIANSWER";
          const allowChangeWhenAnswered =
            isMulti ||
            qTypeUpper === "BETANSWER" ||
            qTypeUpper === "MULTICHOICE" ||
            qTypeUpper === "TRUE/FALSE" ||
            qTypeUpper === "TRUEFALSE" ||
            qTypeUpper === "VANISHINGCHOICE";
          const answerValue = q.answers[answerIdx];
          const answerText = isMulti
            ? String(answerValue).replace(/^\s*\[(.*?)\]\s*$/, "$1")
            : String(answerValue ?? "");
          const isCorrectSingle = !isMulti && answerIdx === 0;
          const isCorrectMulti  = isMulti && /^\[.*\]$/.test(answerValue);
          const isSelected      = selected.includes(displayIdx);

          // TRUE = green, FALSE = red (matches Quizscreen)
          const backgroundColor =
            qTypeNorm === "TRUE/FALSE"
              ? String(answerValue).toUpperCase().includes("TRUE")
                ? "linear-gradient(135deg, #b8f9c2, #2ba44d)" // TRUE → green
                : "linear-gradient(135deg, #ffc1c1, #d73b3e)" // FALSE → red
              : undefined;

// ... inside the return statement of your component

          return (
            <li
              key={displayIdx}
              style={{
                display: "flex",
                justifyContent: "center",
                margin: 0,
                padding: 0,
                listStyle: "none",
                ...(useCompactAnswerRows
                  ? {
                      flex: "1 1 0",
                      minHeight: 0,
                      alignItems: "stretch",
                    }
                  : useCappedAnswerRows && answerRowLayout
                    ? {
                        flex: "0 0 auto",
                        height: answerRowLayout.rowHeight,
                        maxHeight: answerRowLayout.rowHeight,
                        minHeight: 0,
                        alignItems: "stretch",
                      }
                    : {}),
              }}
            >

              <label
                className={
                  "player-answer-option player-option-" +
                  String.fromCharCode(65 + displayIdx).toLowerCase() +
                  (isVanished ? " ghosted" : "") +
                  (timerActive && isSelected ? " selected" : "") +
                  (!timerActive && isSelected && (isCorrectSingle || isCorrectMulti) ? " correct" : "") +
                  (!timerActive && isSelected && !(isCorrectSingle || isCorrectMulti) ? " incorrect" : "") +
                  (!timerActive && !isSelected && (isCorrectSingle || isCorrectMulti) ? " shimmer-correct-unselected" : "")
                }
                style={{
                  width: "min(900px, 86vw)", // fixed max width like Host
                  margin: "6px auto",        // centre in the row
                  fontWeight: 900,
                  borderRadius: "3em",
                  display: "flex",
                  alignItems: "center",
                  minHeight: 0,
                  ...(useCompactAnswerRows
                    ? {
                        flex: "1 1 0",
                        minHeight: 0,
                      }
                    : useCappedAnswerRows
                      ? {
                          flex: "1 1 auto",
                          height: "100%",
                          maxHeight: "100%",
                        }
                      : {}),
                  boxShadow:
                    "inset 0 2px 4px rgba(255,255,255,0.6)," +
                    "inset 0 -2px 6px rgba(0,0,0,0.3)," +
                    "0 4px 12px rgba(0,0,0,0.5)",
                  transition: "all 0.22s cubic-bezier(.42,.0,.58,1)",
                  justifyContent: "center",
                  textAlign: "center",
                  fontFamily: "'Arial Black', Impact, Arial, sans-serif",
                  textShadow: "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
                  opacity: isVanished ? 0.37 : 1,
                  filter: isVanished ? "blur(1.5px)" : "none",
                  pointerEvents: isVanished ? "none" : undefined,
                  background: backgroundColor,
                  cursor:
                    vanishingGhostLocked ||
                    (answered && !allowChangeWhenAnswered) ||
                    timer === 0 ||
                    (q.type === "BETANSWER" && timer > 30)
                      ? "default"
                      : "pointer",
                  position: "relative",
                  overflow: !timerActive && isSelected ? "visible" : undefined,
                }}
              >
                <input
                  type={isMulti ? "checkbox" : "radio"}
                  name="ans"
                  value={displayIdx}
                  checked={isSelected}
                  disabled={
                    isVanished ||
                    vanishingGhostLocked ||
                    (answered && !allowChangeWhenAnswered) ||
                    (isMulti && !isSelected && selected.length >= (q.maxSelectable || 1)) ||
                    timer === 0
                  }
                  onChange={async (e) => {
                    if (
                      isVanished ||
                      vanishingGhostLocked ||
                      (answered && !allowChangeWhenAnswered) ||
                      timer === 0 ||
                      (q.type === "BETANSWER" && timer > 30)
                    ) {
                      return;
                    }

                    if (isMulti) {
                      setSelected((sel) =>
                        e.target.checked
                          ? sel.length < (q.maxSelectable || 1)
                            ? [...sel, displayIdx]
                            : sel
                          : sel.filter((idx) => idx !== displayIdx)
                      );
                      if (qTypeUpper === "MULTIANSWER") {
                        setTimeout(async () => {
                          if (handleSubmitRef.current) {
                            await handleSubmitRef.current();
                          }
                        }, 10);
                      }
                    } else {
                      if (
                        ghostNormType === "VANISHINGCHOICE" &&
                        selected.length >= 1
                      ) {
                        const prevCanon = answerIdxList[selected[0]];
                        if (!vanishingIndexes.includes(prevCanon)) {
                          return;
                        }
                      }
                      setSelected([displayIdx]);
                      if (ghostNormType === "VANISHINGCHOICE") {
                        await handleVanishingChoiceInstantSubmit(
                          answerIdxList[displayIdx]
                        );
                      } else if (
                        (q.type || "").toUpperCase() === "MULTICHOICE" ||
                        (q.type || "").toUpperCase() === "TRUE/FALSE" ||
                        qTypeUpper === "BETANSWER"
                      ) {
                        // Auto-submit MULTICHOICE, TRUE/FALSE, and BETANSWER when answer is selected
                        // Use setTimeout to ensure React state update is processed before submission
                        setTimeout(async () => {
                          if (handleSubmitRef.current) {
                            await handleSubmitRef.current();
                          }
                        }, 10);
                      }
                    }
                  }}
                  style={{ display: "none" }}
                />
                {!timerActive && isSelected && (isCorrectSingle || isCorrectMulti) ? (
                  <span
                    aria-hidden
                    title="Correct"
                    style={{
                      position: "absolute",
                      left: 0,
                      top: "50%",
                      transform: "translate(-50%, -50%)",
                      width: 60,
                      height: 60,
                      borderRadius: "999px",
                      background: "rgba(33,166,74,.92)",
                      border: "2px solid #52d788",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.45)",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "2.1rem",
                      fontWeight: 900,
                      lineHeight: 1,
                      color: "#fff",
                    }}
                  >✓</span>
                ) : !timerActive && isSelected && !(isCorrectSingle || isCorrectMulti) ? (
                  <span
                    aria-hidden
                    title="Incorrect"
                    style={{
                      position: "absolute",
                      left: 0,
                      top: "50%",
                      transform: "translate(-50%, -50%)",
                      width: 60,
                      height: 60,
                      borderRadius: "999px",
                      background: "rgba(214,57,57,.92)",
                      border: "2px solid #ff7a7a",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.45)",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "2.1rem",
                      fontWeight: 900,
                      lineHeight: 1,
                      color: "#fff",
                    }}
                  >✗</span>
                ) : null}
                <PlayerAnswerFitText
                  text={answerText}
                  horizontalInset={!timerActive && isSelected ? 124 : 24}
                  style={{
                    marginLeft: !timerActive && isSelected ? 50 : 0,
                    marginRight: !timerActive && isSelected ? 50 : 0,
                  }}
                />
              </label>
            </li>
          );
        })}
      </ol>
    </AnswersGate>
  );
})()





  
)}


        {/* 👇 ADD THE NEW TIMER HERE 👇 */}
        {shouldShowTimer && (
        <div style={{ flexShrink: 0, width: "100%" }}>
          <ThermoTimerBar q={q} timer={timer} questionNumber={displayQuestionNumber} scale={0.7} /> 
        </div>
        )}


{showPointsPopup && isScoringSlide(qMemo) && pointsReady && (
  <PointsCoin
    value={myPointsThisQuestion}
    ready={pointsReady}
    closing={popupClosing}
  />
)}

{/* Rules card - shows question type, points, and rules */}
{qMemo && normalizeType(qMemo) !== "DISPLAY" && (
  <RulesSlideInCard
    q={qMemo}
    lines={getRulesForQuestion(qMemo)}
    visible={rulesCardVisible}
    questionNumber={displayQuestionNumber}
  />
)}






          <div style={{ minHeight: "1.6vh", width: "100%" }} />
        </form>
      </div>
    );
  }
}
